/**
 * Model anatomy: a layers × roles grid built from metadata alone.
 *
 * This is the landing view for a loaded repo — anatomy first, drill-down
 * second. It answers "what *is* this model, and how was it quantized" before
 * anyone clicks a single tensor.
 *
 * **Zero payload requests.** Everything here is computed from the safetensors
 * headers already fetched for the inventory plus, at most, one `config.json`
 * read. No weight bytes are touched. That is what makes it affordable as a
 * default view rather than something you opt into.
 *
 * **It works for every repo, not just quantized ones.** Where a quantization
 * scheme is recognized, cells carry effective bits-per-weight and a `bits@gs`
 * annotation. Where none is, the same grid becomes a **dtype map** — which is
 * the more useful output than it sounds: a frontier repo whose bulk mass is
 * U8-packed while attention stays BF16 shows that anatomy at a glance, instead
 * of making someone click a tensor and receive a refusal.
 *
 * **Unrecognized names never break the grid.** They fall into an "other" row.
 * A grid that renders 90% of an unfamiliar architecture is worth far more than
 * one that throws because it met a naming convention it didn't know.
 *
 * Per the Round 1 ruling, classification is **transparent**: every cell records
 * which rule matched, so the UI can show its working rather than asserting a
 * component breakdown the reader has to take on faith.
 */

import type { InventoryTensor } from "../safetensors/inventory.js";
import { type BlockScaledGroup, findBlockScaledGroups } from "./block-scaled.js";
import { type Nf4Group, findNf4Groups } from "./bnb-nf4.js";
import { classifyBuffer } from "./buffers.js";
import {
  type PackedWeightGroup,
  findPackedWeightGroups,
} from "./compressed-tensors.js";
import { type E8m0ScaledGroup, findE8m0ScaledGroups } from "./e8m0-scaled.js";
import { type EschaMoeGroup, findEschaMoeGroups } from "./eschamoe.js";
import { findExl3Groups } from "./exl3-trellis.js";
import { type Int8ScaledGroup, findInt8ScaledGroups } from "./int8-scaled.js";
import {
  type ArchitectureAnchors,
  type DeclaredQuantization,
  findAffineGroups,
} from "./mlx.js";
import { findPackedIntGroups } from "./packed-int.js";

/** Where a tensor sits in the model, and how we decided that. */
export interface TensorPlacement {
  /** Repeating block index, or `undefined` for anything outside the stack. */
  readonly layer: number | undefined;
  /** Column label: `q_proj`, `mlp.down`, `embed`, `other`, … */
  readonly role: string;
  /** The rule that matched, shown to the reader so the grid can justify itself. */
  readonly rule: string;
}

/**
 * Name-shape rules, in priority order.
 *
 * Deliberately a readable table rather than clever parsing: architectures name
 * things differently and the honest way to cover them is to enumerate what we
 * have actually seen. Each entry carries the human-readable rule text that ends
 * up on screen.
 */
const ROLE_RULES: ReadonlyArray<{
  readonly pattern: RegExp;
  readonly role: string;
  readonly rule: string;
}> = [
  // Attention projections — the split ones (Llama/Qwen/Mistral family).
  { pattern: /\bq_proj/, role: "attn.q", rule: "name contains q_proj" },
  { pattern: /\bk_proj/, role: "attn.k", rule: "name contains k_proj" },
  { pattern: /\bv_proj/, role: "attn.v", rule: "name contains v_proj" },
  /*
   * No trailing `\b` on these four — the sixth and seventh instances of the
   * underscore trap. `sensenova/…-MoT-Preview` suffixes every duplicated module
   * `_mot_gen`, so `q_proj_mot_gen` (168 tensors, 13.5% of that repo) matched
   * nothing. Measured over 1 597 524 corpus names, dropping the trailing
   * boundary gains exactly those 168 plus gpt-oss's 108
   * `down_proj_{blocks,scales,bias}`, and steals nothing.
   *
   * The LEADING `\b` stays, and that is not symmetry-for-its-own-sake: dropping
   * it would claim Kimi's `routed_expert_down_proj` (an expert-bank compression
   * wrapper, positionally the opposite of a feed-forward down) and every
   * `gate_up_proj` in the corpus, where `mlp.up` would be exactly half true.
   */
  { pattern: /\b[o]_proj/, role: "attn.o", rule: "name contains o_proj" },
  // GPT-2 style fuses QKV into one tensor and names projections c_attn/c_proj.
  { pattern: /attn\.c_attn/, role: "attn.qkv", rule: "attn.c_attn (fused QKV)" },
  { pattern: /attn\.c_proj/, role: "attn.o", rule: "attn.c_proj (output proj)" },
  // MLP / feed-forward.
  { pattern: /\bgate_proj\b|\bw1\b/, role: "mlp.gate", rule: "gate_proj / w1" },
  { pattern: /\bup_proj\b|\bw3\b/, role: "mlp.up", rule: "up_proj / w3" },
  { pattern: /\bdown_proj\b|\bw2\b/, role: "mlp.down", rule: "down_proj / w2" },
  { pattern: /mlp\.c_fc/, role: "mlp.up", rule: "mlp.c_fc (GPT-2 up-projection)" },
  {
    pattern: /mlp\.c_proj/,
    role: "mlp.down",
    rule: "mlp.c_proj (GPT-2 down-projection)",
  },
  // Mixture-of-experts routing shows up as its own role rather than "other";
  // on MoE models it is where a surprising share of the parameters live.
  /*
   * Do not "fix" the underscore trap here without reading this.
   *
   * `\bgate\b` fails on `gate_up_proj` because `_` is a word character, so no
   * boundary fires after `gate` and the `(?!_proj)` lookahead is never even
   * reached. That accident is currently the only thing stopping this rule from
   * claiming every fused feed-forward tensor in the corpus as a router. The
   * natural-looking hardening — `(?:^|[._])gate(?!_proj)` — would do exactly
   * that. `SYNONYM_RULES` now claims those names first, which protects against
   * it, but the rule below is still a loaded gun pointed at `mlp.gate_up`.
   */
  { pattern: /\bgate\b(?!_proj)|router/, role: "moe.router", rule: "gate / router" },
  // Normalization. Cheap in bytes but worth a column: norms are usually the
  // tensors a quantizer deliberately leaves alone, which is itself informative.
  {
    /*
     * `[._]norm` and not `\bnorm\b`.
     *
     * `_` is a word character, so `\b` never fires between `q` and `norm` in
     * `self_attn.q_norm` — QK-norm tensors were landing in `other`, eight per
     * attention layer, invisible until the mixer rules cleared everything else
     * out of that row. This is the same trap the reviewer pointed at in
     * `\bo_proj\b` failing to match `out_proj` (DISCOURSE, 2026-08-08); worth
     * assuming it is lurking in any rule here that relies on `\b` next to an
     * underscore.
     */
    /*
     * Two further variants of the same trap, both found by surveying real
     * names (2026-08-08), and both fixed here rather than as synonym rows
     * because the rule was already meant to cover them:
     *
     * - **Case.** `/layernorm/` never matched BERT's `LayerNorm`, so every
     *   BERT-lineage checkpoint had its norms in `other`. Verified safe across
     *   2.48M cached tensor names: the entire delta from `i` is the fragment
     *   `LayerNorm`, 1486 tensors, every one a genuine LayerNorm. It also
     *   picks up the legacy TensorFlow `LayerNorm.gamma`/`.beta`, which carry
     *   no `weight`/`bias` suffix at all.
     * - **Digits.** `\b` does not fire between `norm` and `1` either, so
     *   `norm1`/`norm2` were unnamed on every timm ViT and DINOv2 block.
     *   `\d*` before the boundary covers them without matching
     *   `normalization`, where the boundary still fails.
     */
    /*
     * And the trailing half of the same trap: `norm_mot_gen`, `norm_conv`,
     * `norm_self_att`, `pre_fc_norm_hidden`, `norm_pre_attn` — 801 names across
     * 24 repos, 16 of which were sitting in `bias`. `(?!_(?:mean|std)\b)`
     * declines `input_conditioner.norm_mean`/`norm_std`, which are image
     * preprocessing constants rather than a LayerNorm's parameters.
     */
    pattern:
      /layernorm|layer_norm|\bln_\d|(?:^|[._])norm\d*(?:_(?!mean\b|std\b)[a-z0-9]+)*\b|rmsnorm/i,
    role: "norm",
    rule: "norm / layernorm",
  },
  // Embeddings and the output head — often the largest single tensors, and
  // often quantized differently from the stack.
  {
    pattern: /embed_tokens|\bwte\b|word_embeddings|tok_embeddings/,
    role: "embed",
    rule: "embedding table",
  },
  /*
   * `embedding` / `unembedding` — `gpt-oss`'s `original/` spelling.
   *
   * Rows rather than shape reasoning, per the reviewer's split: the fused
   * feed-forward pair needed arithmetic because its names carry no meaning
   * (`mlp1`, `mlp2` are positional), while these two names say exactly what
   * they are. Reaching for the shape tier here would be ceremony.
   *
   * **`unembedding` must not match the `embedding` rule**, and the leading
   * `(?:^|\.)` is what stops it: in `unembedding.weight` the substring
   * `embedding` is preceded by `n`, so there is no anchor for it to attach to.
   * Without that, the output head would be counted as a second embedding table
   * — one tensor of 201088 x 2880 filed under the wrong role, which is a
   * misplacement large enough to move the map's whole global column.
   */
  {
    pattern: /(?:^|\.)embedding(?:\.(?:weight|bias))?$/,
    role: "embed",
    rule: "embedding (gpt-oss original)",
  },
  {
    pattern: /(?:^|\.)unembedding(?:\.(?:weight|bias))?$/,
    role: "lm_head",
    rule: "unembedding (gpt-oss original)",
  },
  {
    pattern: /\bwpe\b|position_embeddings/,
    role: "pos-embed",
    rule: "position embedding",
  },
  { pattern: /lm_head|output\.weight$/, role: "lm_head", rule: "lm_head / output" },
  { pattern: /\bbias\b/, role: "bias", rule: "bias" },
];

/** Matches the layer index in the common naming conventions. */
const LAYER_PATTERNS: readonly RegExp[] = [
  // `(?:^|\.)` rather than a bare `\.`: GPT-2 names its blocks `h.3.attn…`
  // with no prefix at all, so a pattern anchored on a leading dot silently
  // reports "no layer" for an entire architecture.
  /(?:^|\.)layers\.(\d+)\./,
  /(?:^|\.)h\.(\d+)\./,
  /(?:^|\.)blocks?\.(\d+)\./,
  /*
   * `blk.N.` is llama.cpp's, and therefore every GGUF file's. Absent, a GGUF
   * model has no layer on any tensor and the entire stack collapses into the
   * global column — a map that renders, looks like a one-layer model, and is
   * wrong about the only axis it has.
   *
   * `blocks?` above does not cover it: that matches `block.` and `blocks.`, and
   * `blk` is neither.
   */
  /(?:^|\.)blk\.(\d+)\./,
  /(?:^|\.)layer\.(\d+)\./,
  /(?:^|\.)transformer\.(\d+)\./,
];

/** A tensor's position in the layer stack, and the name prefix that says so. */
export interface LayerGroup {
  /** The layer index, or `undefined` for anything outside the stack. */
  readonly layer: number | undefined;
  /**
   * The leading substring every tensor in this layer shares, including the
   * trailing dot — `model.layers.0.`, `h.7.`. Empty string when there is no
   * layer, which is also what makes it usable as a grouping key.
   */
  readonly prefix: string;
  /**
   * Everything *before* the layer keyword, trailing dot included —
   * `model.language_model.` for `model.language_model.layers.7.mlp…`, and `""`
   * for `h.7.…`, which starts with its own stack.
   *
   * This is the field that tells two layer stacks apart. A multimodal
   * checkpoint has several — a text stack, a vision stack, an audio stack —
   * each numbered from zero, so `layer` alone is ambiguous across a whole
   * model and only means something *within* one root. See `analysis/towers.ts`,
   * which is the only caller that needs it.
   */
  readonly stackRoot: string;
}

/**
 * Split a tensor name into "which layer" and "the rest".
 *
 * Exists so the tensor list can group and de-prefix its rows using the *same*
 * patterns the anatomy map places cells with. Two independent notions of what
 * counts as a layer would be two things to keep in step, and they would drift on
 * exactly the architectures the patterns were widened for.
 */
export function layerGroupOf(name: string): LayerGroup {
  for (const pattern of LAYER_PATTERNS) {
    const match = pattern.exec(name);
    if (match?.[1] !== undefined) {
      return {
        layer: Number.parseInt(match[1], 10),
        // `match.index` is where `(?:^|\.)` started, so the slice covers the
        // separator, the keyword, the index and the trailing dot.
        prefix: name.slice(0, match.index + match[0].length),
        // `(?:^|\.)` matched a literal dot unless the stack starts the name, and
        // that dot belongs to the root rather than to the keyword — keeping it
        // is what makes the root a usable `startsWith` test that cannot match a
        // sibling whose name merely begins with the same characters.
        stackRoot: match[0].startsWith(".") ? name.slice(0, match.index + 1) : "",
      };
    }
  }
  return { layer: undefined, prefix: "", stackRoot: "" };
}

/**
 * The token mixers this file can name structurally, and how to spot one.
 *
 * A "token mixer" is whatever a block uses to move information between
 * positions. In a transformer that is attention; in the hybrids that have
 * become common since 2025 it is a gated-linear or state-space block, and those
 * are named nothing like `q_proj`.
 *
 * `signature` is what marks a *layer* as containing a mixer. It is checked
 * against the layer's whole set of tensor names, not against one name, which is
 * the reviewer's point (DISCOURSE, 2026-08-08): `out_proj` beside
 * `q/k/v_proj` is attention's output projection, while `out_proj` beside
 * `in_proj_* + conv1d` is the mixer's. A name-only rule cannot tell those
 * apart and would have to guess; a sibling check simply knows.
 */
/*
 * `[._]conv1d\b`, not `\bconv1d\b` — the underscore trap re-gating whole layers.
 *
 * `_` is a word character, so no boundary fires between `k` and `conv1d` in
 * `self_attn.k_conv1d`. Measured over the cached corpus: the old pattern caught
 * **102 of 570** `conv1d` names, missing all 69 of `moonshotai/Kimi-K3`'s
 * gated-delta-net layers, whose entire mixer apparatus was therefore invisible.
 *
 * The naive widening to bare `conv1d` catches all 570 — and that is wrong. The
 * extra 144 are `gemma-4`'s `audio_tower.lconv1d`, a lightweight audio
 * convolution rather than a token mixer, and gating those layers as mixers
 * would mis-classify two of Google's flagship repos. Requiring a separator
 * before `conv1d` catches Kimi and declines gemma: 426 names.
 *
 * This is a *signature*, so widening it re-gates an entire layer's
 * classification rather than one tensor. It was landed on its own and measured
 * with `pnpm coverage --from …` for exactly that reason.
 */
const MIXER_SIGNATURE =
  /[._]conv1d\b|\bin_proj|\bssm_(?:in|conv1d)\b|\bconv\.depthwise\b/;

/** Within a mixer layer, which structural position a tensor occupies. */
const MIXER_PARTS: ReadonlyArray<{
  readonly pattern: RegExp;
  readonly part: string;
  readonly rule: string;
}> = [
  { pattern: /[._]conv1d\b/, part: "conv", rule: "mixer layer: conv1d" },
  { pattern: /\bin_proj/, part: "in", rule: "mixer layer: in_proj*" },
  { pattern: /\bout_proj\b/, part: "out", rule: "mixer layer: out_proj" },
  {
    pattern: /\b[ABD]_log\b|\bdt_bias\b|\bA\b|\bD\b/,
    part: "state",
    rule: "mixer layer: state params",
  },
  /*
   * llama.cpp's spelling of the same four positions. GGUF flattens the HF
   * module path into one underscore-joined leaf, so `mixer.in_proj.weight`
   * becomes `ssm_in.weight` and matches none of the rules above — `\bin_proj`
   * needs the word `in_proj`, and `\bA\b` needs `A` standing alone where GGUF
   * writes `ssm_a`.
   *
   * Placed after the HF rules and written to be mutually exclusive with them, so
   * a name can only ever satisfy one: an abstention on two disagreeing rules is
   * the resolver's safety property and is worth not spending.
   */
  /*
   * The conformer convolution module, in llama.cpp's speech spelling.
   *
   * Reviewer ruling (2026-08-09), and the split within the module is theirs:
   * **only the depthwise convolution is the token mixer** — it is what moves
   * information between positions — while `pointwise1` and `pointwise2` are 1x1
   * channel projections, which is to say the module's in and out.
   *
   * `mixer.*` is not a category error even though a conformer block *also* has
   * attention. The gemma precedent already settled that a temporal token mixer
   * is a temporal token mixer, and coexistence within one block is carried by
   * the column, exactly as the mechanism-span ruling resolved for Kimi: rows
   * name structure, columns show composition, the caption discloses.
   */
  { pattern: /\bconv\.depthwise\b/, part: "conv", rule: "conformer: conv.depthwise" },
  { pattern: /\bconv\.pointwise1\b/, part: "in", rule: "conformer: conv.pointwise1" },
  { pattern: /\bconv\.pointwise2\b/, part: "out", rule: "conformer: conv.pointwise2" },
  { pattern: /\bssm_conv1d\b/, part: "conv", rule: "GGUF mixer: ssm_conv1d" },
  { pattern: /\bssm_in\b/, part: "in", rule: "GGUF mixer: ssm_in" },
  { pattern: /\bssm_out\b/, part: "out", rule: "GGUF mixer: ssm_out" },
  {
    /*
     * `alpha` and `beta` are not in llama.cpp's core vocabulary — they were read
     * off `prism-ml/Ternary-Bonsai-27B-gguf`, where 96 of 851 tensors carry them
     * and nothing else claimed them. Enumerated rather than widened to
     * `ssm_[a-z]+`, on the same reasoning the HF table follows: a rule written
     * against names actually observed can be checked; one written against a
     * pattern nobody has seen claims tensors we have never looked at.
     *
     * `mixer.state` says what they do structurally without claiming what they
     * are, which is the honest answer for a coefficient we have not seen
     * documented.
     */
    pattern: /\bssm_(?:a|d|dt|x|alpha|beta)\b/,
    part: "state",
    rule: "GGUF mixer: ssm state params",
  },
];

/**
 * Where a tensor sits in the grid.
 *
 * `siblings` is every tensor name in the same layer, and is what lets a rule
 * use context rather than guess from a name. It is optional so that callers
 * with one name and no layer — tests, and any future single-tensor use — still
 * get the name-only behaviour that has always been here.
 *
 * `family` renames the mixer rows once `config.json` has said what the
 * architecture is: `linattn.in` rather than `mixer.in`. Absent, the rows stay
 * `mixer.*`, which is the honest unknown — it says what the thing *does*
 * without claiming what it *is*, and is strictly better than `other`.
 */
/**
 * How confidently a name was placed — the reviewer's confidence tiering
 * (DISCOURSE, 2026-08-08).
 *
 * Recorded per tensor and never shown as a per-cell "probably" marker: those
 * spend pixels teaching a reader to distrust the map. It surfaces as hover
 * provenance, and it is what the whole-model post-pass uses to decide which
 * claims it is entitled to demote.
 *
 * Ordered weakest-last, which is also resolver order.
 */
export type ClassificationTier = "family" | "synonym" | "signature" | "shape";

export interface Classification {
  readonly role: string;
  readonly rule: string;
  readonly tier: ClassificationTier;
}

/**
 * The unit of classification: one repeating layer, or the global pseudo-block.
 *
 * **Why a block and not a name.** `classifyTensor(name)` was a name → role
 * function, and sibling context had already been bolted onto it as an optional
 * parameter to disambiguate `out_proj`. Shape reasoning breaks the signature
 * outright: recognizing that `wq_a [d×r]` and `wq_b [r×d′]` are a low-rank pair
 * requires *both* tensors and their shapes, so no function of one name can ever
 * see it. `deepseek-ai/DeepSeek-V4-Flash-0731` factors all four attention
 * projections that way, which is why its entire attention mechanism was
 * unnamed while 95% of its bytes were named.
 *
 * The global pseudo-block (`layer: undefined`) carries the embedding, the head
 * and the final norm. Including it here rather than special-casing it is what
 * lets tied and untied embeddings fall out of the same mechanism without any
 * resolver needing whole-model context.
 */
export interface LayerBlock {
  /** Layer index, or `undefined` for everything outside the stack. */
  readonly layer: number | undefined;
  readonly tensors: readonly InventoryTensor[];
  /** From `config.json`'s `model_type`, where one was recognized. */
  readonly family: string | undefined;
  /**
   * What `config.json` states about the architecture's shape.
   *
   * Only the shape resolver reads it, and only to *promote* a pair it already
   * found — never to find one. Absent config means unpositioned pairs, not
   * missing pairs.
   */
  readonly anchors: ArchitectureAnchors | undefined;
}

/** What a resolver returns for the tensors it is confident about. */
type Placements = ReadonlyMap<string, { readonly role: string; readonly rule: string }>;

/**
 * One stage of the classification pipeline.
 *
 * The pipeline stays deliberately dumb — ordered, first-confident-answer-wins,
 * every resolver declaring its own tier, no side channels between them. All of
 * this is currently table interpretation; the shape reasoner, when it lands,
 * will be the one resolver that is genuinely code.
 *
 * A resolver returning nothing for a tensor is not a failure, it is an
 * abstention, and the next resolver gets its turn.
 */
interface Resolver {
  readonly tier: ClassificationTier;
  readonly resolve: (block: LayerBlock) => Placements;
}

/**
 * Mixer parts, gated on the layer's own signature.
 *
 * Runs before the name table because `in_proj_qkv` matches nothing there while
 * `out_proj` is a near-miss for the attention rules. Gating on the signature
 * means an ordinary attention layer never reaches these rules at all.
 */
const signatureResolver: Resolver = {
  tier: "signature",
  resolve(block) {
    const out = new Map<string, { role: string; rule: string }>();
    if (!block.tensors.some((t) => MIXER_SIGNATURE.test(t.name))) return out;
    for (const tensor of block.tensors) {
      for (const rule of MIXER_PARTS) {
        if (rule.pattern.test(tensor.name)) {
          out.set(tensor.name, {
            role: `${block.family ?? "mixer"}.${rule.part}`,
            rule: rule.rule,
          });
          break;
        }
      }
    }
    return out;
  },
};

/**
 * Gemma 4's per-layer embeddings, which are half of what an E2B file weighs.
 *
 * ## What PLE is, and why it is not a footnote
 *
 * Beside the ordinary token embedding, the architecture carries a *second*
 * lookup table with one slice per layer: `[vocab, layers × 256]`. Each layer
 * reads its slice, gates it, projects it into the hidden width and adds it to
 * the residual. `fraQtl/Gemma-4-E2B-it-Hi-Fi-GGUF` spends **46.5% of the file
 * on that one tensor** — the model's headline feature was its largest unnamed
 * mass.
 *
 * The shapes prove the reading without anything external: `inp_gate` is
 * 1536→256, `proj` is 256→1536, `per_layer_model_proj` is 1536→8960, and
 * 8960 = 35 layers × 256. E4B says 10752 = 42 × 256.
 *
 * ## Why a signature resolver and not synonym rows
 *
 * Five of the six names are unambiguous enough for the table. The sixth is
 * `blk.N.proj.weight`, and a rule for a bare `proj` leaf is a loaded gun.
 * Measured over the 6048 cached corpus bodies, `*.proj.{weight,bias}` is:
 *
 *     1565x  32 repos  attn.proj          ← ViT attention *output* projection
 *      120x   2 repos  router.proj        ← gemma-4 MoE router
 *       64x  34 repos  patch_embed.proj
 *       48x   1 repo   self_attn.proj
 *        6x   3 repos  confidence_head.proj
 *
 * An ungated row would claim ~1800 tensors across 60+ repos, most of them as
 * the exact opposite of what they are. So the rules fire only inside a block
 * that has already identified itself as PLE by its *siblings* — which is what
 * `signatureResolver` does for mixers, and the same argument.
 *
 * The reviewer asked for GGUF's `general.architecture` as the gate. Sibling
 * evidence is what is available today — the architecture string is read in
 * `gguf/header.ts` for anchors and never reaches `Inventory` — and it is
 * sufficient here: every collision above carries a qualifying segment
 * (`attn.`, `router.`, `patch_embed.`) that a PLE block does not have. Plumbing
 * the architecture through is worth doing and is a separate change; it would
 * narrow this rule, not correct it.
 *
 * ## The safetensors half moves no percentage and is the more important half
 *
 * `embed_tokens_per_layer` already matched `/embed_tokens/` and was being
 * counted as the ordinary embedding table. That is *named and wrong*: the same
 * model in two formats attributed the same 4.375 GiB to two different rows, so
 * compare mode showed the page disagreeing with itself. Naming it here — before
 * the vocabulary table gets to it — is what makes the two formats agree.
 */
/*
 * Every marker here is a name that only PLE produces, and the layer-output
 * scalar is deliberately **not** one of them.
 *
 * It was, and that made the rule self-triggering: `layer_output_scale` gated the
 * block *and* was claimed by it, so the evidence and the conclusion were the
 * same tensor. `unsloth/gemma-4-26B-A4B-it-GGUF`'s MTP draft — 49 tensors, no
 * per-layer table anywhere in the file — came out with a `ple.scale` row, which
 * is the map inventing a mechanism the model does not have.
 *
 * The corpus is why that matters beyond the bug: `layer_scalar` /
 * `layer_output_scale` appears on 12 repos, including Gemma 4 variants with no
 * PLE at all, so it is ordinary layer-output scaling rather than PLE machinery.
 * The reviewer deferred it to the scalar row-family review; this is the
 * measurement that says they were right to.
 */
const PLE_SIGNATURE =
  /\bper_layer_(?:input_gate|projection|token_embd|model_proj)|\bembed_tokens_per_layer\b|\binp_gate\b/;

/**
 * The five positions, in both spellings. HF names are module paths and GGUF
 * flattens them to an underscore leaf, exactly as elsewhere in this file.
 *
 * None of these reaches the two norms — `per_layer_projection_norm` and
 * `per_layer_proj_norm` — and that is boundary behaviour, not luck: `_` is a
 * word character, so `\bper_layer_projection\b` cannot match
 * `per_layer_projection_norm`. They stay `norm`, which is what they are.
 */
const PLE_PARTS: ReadonlyArray<{
  readonly pattern: RegExp;
  readonly part: string;
  readonly rule: string;
}> = [
  {
    pattern: /\bper_layer_token_embd\b|\bembed_tokens_per_layer\b/,
    part: "embed",
    rule: "PLE: per-layer embedding table",
  },
  {
    pattern: /\bper_layer_model_proj(?:ection)?\b/,
    part: "model_proj",
    rule: "PLE: hidden → per-layer projection",
  },
  {
    pattern: /\bper_layer_input_gate\b|\binp_gate\b/,
    part: "gate",
    rule: "PLE: per-layer input gate",
  },
  {
    /*
     * The dangerous one, anchored two ways. The HF spelling names itself; the
     * GGUF spelling is a bare `proj` and is only accepted directly under a
     * `blk.N.` block, which is where no collision in the corpus lives.
     */
    pattern: /\bper_layer_projection\b|(?:^|\.)blk\.\d+\.proj\.(?:weight|bias)$/,
    part: "proj",
    rule: "PLE: per-layer → hidden injection projection",
  },
];

const pleResolver: Resolver = {
  tier: "signature",
  resolve(block) {
    const out = new Map<string, { role: string; rule: string }>();
    if (!block.tensors.some((t) => PLE_SIGNATURE.test(t.name))) return out;
    for (const tensor of block.tensors) {
      for (const rule of PLE_PARTS) {
        if (rule.pattern.test(tensor.name)) {
          out.set(tensor.name, { role: `ple.${rule.part}`, rule: rule.rule });
          break;
        }
      }
    }
    return out;
  },
};

/**
 * The name table — vocabulary, in the reviewer's layer-0 sense.
 *
 * `ROLE_RULES` already *is* a synonym table; it just happens to know exactly one
 * vocabulary, the Llama/Qwen one (`q_proj`, `gate_proj`, `down_proj`). Measured
 * over the top 100 downloads that covers `text-generation` at 82.5% and
 * `fill-mask` at 38.2%, because BERT spells the same components differently.
 * Widening it is data, not code.
 *
 * First match wins, preserving the order the table is written in. The
 * abstain-on-ambiguity rule the reviewer specified belongs to the *new* synonym
 * rows and is deliberately not applied here yet: turning it on for these rules
 * in the same change that restructures them would make any coverage movement
 * impossible to attribute.
 */
const vocabularyResolver: Resolver = {
  tier: "synonym",
  resolve(block) {
    const out = new Map<string, { role: string; rule: string }>();
    for (const tensor of block.tensors) {
      for (const rule of ROLE_RULES) {
        if (rule.pattern.test(tensor.name)) {
          out.set(tensor.name, { role: rule.role, rule: rule.rule });
          break;
        }
      }
    }
    return out;
  },
};

/** A tensor's module: its path with the parameter suffix and leaf removed. */
function moduleOfName(name: string): string {
  const base = name.replace(/\.(?:weight|bias)$/, "");
  const cut = base.lastIndexOf(".");
  return cut === -1 ? "" : base.slice(0, cut);
}

/**
 * Feed-forward pairs whose numbering starts at zero.
 *
 * `nvidia/LocateAnything-3B` and Kimi-K3's vision tower spell their block FFN
 * `mlp.fc0` / `mlp.fc1`, where **`fc1` is the down projection** — while Whisper,
 * BART and CLIP spell theirs `fc1` / `fc2`, where `fc1` is the up projection.
 * The same name means opposite things, and we were labelling the 0-indexed
 * `fc1` as `mlp.up` on 81 tensors across 3 repos.
 *
 * A synonym row cannot fix it: adding `fc0`→up and `fc1`→down makes two rows
 * disagree about `fc1`, the resolver abstains, and every Whisper/BART/CLIP FFN
 * loses its name — a large loss to correct a small one.
 *
 * So it is decided by *siblings*, which is what that tier is for: the
 * disambiguating evidence is the presence of `fc0` in the same module, and it
 * lives in the name set rather than in the name. Per the reviewer's ruling
 * (2026-08-08): `fc0`+`fc1` → up/down here; `fc1`+`fc2` is left entirely to the
 * synonym rows; a bare `fc1` falls through unchanged. No confidence marker —
 * within a module this is a fact, not an inference.
 */
const zeroIndexedFfnResolver: Resolver = {
  tier: "signature",
  resolve(block) {
    const out = new Map<string, { role: string; rule: string }>();
    const modules = new Set(
      block.tensors
        .filter((t) => /(?:^|[._])fc0\./.test(t.name))
        .map((t) => moduleOfName(t.name)),
    );
    if (modules.size === 0) return out;

    for (const tensor of block.tensors) {
      const match = /(?:^|[._])fc([01])\./.exec(tensor.name);
      if (!match || !modules.has(moduleOfName(tensor.name))) continue;
      out.set(
        tensor.name,
        match[1] === "0"
          ? { role: "mlp.up", rule: "fc0 (0-indexed FFN input)" }
          : { role: "mlp.down", rule: "fc1 beside fc0 (0-indexed FFN output)" },
      );
    }
    return out;
  },
};

/** Ordered, strongest first. `family` and `shape` resolvers are still to come. */
/**
 * Layer 0: vocabulary aliasing — the same components, spelled differently.
 *
 * ## What this is
 *
 * `ROLE_RULES` knows one vocabulary, the Llama/Qwen one. Measured over the top
 * 100 downloads that covered `text-generation` at 82.5% and `fill-mask` at
 * 38.2%: `google-bert/bert-base-uncased` was 78.2% unnamed, and we named its
 * *biases* while missing its attention. These rows are the same fourteen roles
 * under the spellings the rest of the Hub uses. No new roles, no config, no
 * shape reasoning.
 *
 * Derived from four parallel surveys of the real tensor names in the cached
 * corpus (2026-08-08), not from memory of what these architectures are called.
 *
 * ## Why patterns and not `model_type`
 *
 * The reviewer's ruling: roberta, electra and distilbert share BERT's spelling,
 * so one vocabulary row covers them where family rows would need one each. It
 * also means a fine-tune with an unrecognized `model_type` is still named.
 *
 * ## Deduplicated on purpose
 *
 * The surveys independently proposed overlapping rows — three of them derived
 * `attention.output.dense` → `attn.o`, and two derived the same non-obvious fix
 * for the `output.dense` collision below. Landing them as separate rows would
 * make two patterns match one name, and this resolver **abstains** on that, so
 * merging blindly would have driven ViT and CLAP coverage *down*. The table is
 * a merged set, not a concatenation.
 */
const SYNONYM_RULES: ReadonlyArray<{
  readonly pattern: RegExp;
  readonly role: string;
  readonly rule: string;
}> = [
  /* -- llama.cpp / GGUF --------------------------------------------------- *
   *
   * One convention rather than a hundred, which is why this is a table and not
   * a research project: llama.cpp's converters normalise every architecture
   * onto these names, so a rule here holds across every publisher that ships
   * GGUF rather than for one lineage.
   *
   * The shape is different from the HF rules above in one way worth noticing.
   * HF names are module *paths* — `model.layers.0.self_attn.q_proj.weight` —
   * and the rules match a path segment. GGUF flattens the path into a single
   * underscore-joined leaf, so these match a leaf and `\b` boundaries do the
   * work that dots do above. That is also what keeps them from colliding:
   * `\battn_q\b` does not match `attn_q_norm` or `attn_q_a`, because `_` is a
   * word character.
   */

  // Outside the stack.
  { pattern: /\btoken_embd\b(?!_norm)/, role: "embed", rule: "GGUF: token_embd" },
  { pattern: /\bposition_embd\b/, role: "embed", rule: "GGUF: position_embd" },
  { pattern: /\btoken_embd_norm\b/, role: "norm", rule: "GGUF: token_embd_norm" },
  { pattern: /\boutput_norm\b/, role: "norm", rule: "GGUF: output_norm" },
  /*
   * The per-layer output multiplier — Gemma 4's, in both spellings.
   *
   * Reviewer ruling (2026-08-11, gate): **its own row, named for what the
   * checkpoint says it is.** Not `norm`, which would be a category error that
   * *creates* a mechanism span — the safetensors side carries real norms *and*
   * `layer_scalar` as distinct tensors, so folding them merges two mechanisms in
   * one format and breaks cross-format parity with the other. And not a generic
   * `scalar` family: the reviewer tracks no other members, so a family row for
   * one member would lose exactly the identity the vocabulary rule exists to
   * keep. V4-Flash's `attn_sink` is the nearest candidate and does not
   * preemptively join anything.
   *
   * Domain note from the ruling: in Gemma 4 this is applied after the residual
   * path — downstream of PLE injection where PLE exists, and **present
   * regardless**, which is what the no-PLE 26B variant showed from the outside
   * and why this is not a `ple.*` row. A provisional `ple.scale` was tried and
   * refuted within the hour; see `PLE_SIGNATURE`.
   *
   * A synonym row rather than a signature rule, because both names are
   * self-describing: unlike `blk.N.proj`, neither collides with anything in the
   * corpus, so there is nothing for sibling context to disambiguate.
   */
  {
    pattern: /\blayer_output_scale\b|\blayer_scalar\b/,
    role: "layer_scale",
    rule: "per-layer output scale",
  },
  /*
   * Rotary frequencies. Not a scalar and not part of that question — one F32
   * table llama.cpp writes on every architecture, unnamed until now. Reviewer:
   * "no objection, self-describing".
   */
  { pattern: /\brope_freqs\b/, role: "rope", rule: "GGUF: rope_freqs" },
  /*
   * `output.weight` is the untied LM head, and llama.cpp uses it for every
   * architecture. It is also what original Llama checkpoints call the same
   * tensor, so this rule is not GGUF-only and is correct in both worlds.
   */
  {
    pattern: /(?:^|\.)output\.(?:weight|bias)$/,
    role: "lm_head",
    rule: "GGUF: output",
  },

  // Attention.
  { pattern: /\battn_q\b/, role: "attn.q", rule: "GGUF: attn_q" },
  { pattern: /\battn_k\b/, role: "attn.k", rule: "GGUF: attn_k" },
  { pattern: /\battn_v\b/, role: "attn.v", rule: "GGUF: attn_v" },
  { pattern: /\battn_qkv\b/, role: "attn.qkv", rule: "GGUF: attn_qkv" },
  { pattern: /\battn_output\b/, role: "attn.o", rule: "GGUF: attn_output" },
  { pattern: /\battn_gate\b/, role: "attn.gate", rule: "GGUF: attn_gate" },
  /*
   * Every `*_norm` under attention, including the per-head Q/K norms that have
   * become standard since 2025. Listed before the low-rank pairs below so a
   * `attn_q_a_norm` is a norm rather than half of a factorised Q.
   */
  {
    pattern: /\battn_(?:norm|norm_2|q_norm|k_norm|q_a_norm|kv_a_norm|sub_norm)\b/,
    role: "norm",
    rule: "GGUF: attn_*_norm",
  },
  /*
   * DeepSeek-style factorised attention, in llama.cpp's spelling. Named to the
   * same rows the HF path uses for the same tensors, so a GGUF conversion of a
   * model and its safetensors original produce the same map.
   */
  { pattern: /\battn_q_a\b/, role: "attn.q", rule: "GGUF: attn_q_a (MLA down)" },
  { pattern: /\battn_q_b\b/, role: "attn.q", rule: "GGUF: attn_q_b (MLA up)" },
  {
    pattern: /\battn_kv_a_mqa\b/,
    role: "attn.kv",
    rule: "GGUF: attn_kv_a_mqa (MLA down)",
  },
  { pattern: /\battn_kv_b\b/, role: "attn.kv", rule: "GGUF: attn_kv_b (MLA up)" },

  // Feed-forward, dense and mixture-of-experts.
  { pattern: /\bffn_norm\b/, role: "norm", rule: "GGUF: ffn_norm" },
  /*
   * The router first, and deliberately before the `ffn_gate` rule: llama.cpp
   * calls it `ffn_gate_inp`, which shares a prefix with the gate projection and
   * means something entirely different. `\b` does not separate them — `_` is a
   * word character — so `ffn_gate` is written with a negative lookahead rather
   * than trusted to boundary matching.
   */
  {
    pattern: /\bffn_gate_inp(?:_shexp)?\b/,
    role: "moe.router",
    rule: "GGUF: ffn_gate_inp (router)",
  },
  {
    pattern: /\bexp_probs_b\b/,
    role: "moe.router",
    rule: "GGUF: exp_probs_b (router bias)",
  },
  {
    pattern: /\bffn_gate(?!_inp)(?:_exps|_shexp)?\b/,
    role: "mlp.gate",
    rule: "GGUF: ffn_gate",
  },
  /*
   * The fused gate+up projection, which `gemma-4` uses for its experts and which
   * `ffn_gate` cannot claim: `\bffn_gate` is followed by `_up`, and `_` is a word
   * character, so no boundary separates them. Measured on
   * `unsloth/gemma-4-26B-A4B-it-GGUF`, where these 30 tensors are **half the
   * file's bytes** — the single largest naming gap the GGUF spread found.
   */
  {
    pattern: /\bffn_gate_up(?:_exps|_shexp)?\b/,
    role: "mlp.gate_up",
    rule: "GGUF: ffn_gate_up (fused)",
  },
  { pattern: /\bffn_up(?:_exps|_shexp)?\b/, role: "mlp.up", rule: "GGUF: ffn_up" },
  {
    pattern: /\bffn_down(?:_exps|_shexp)?\b/,
    role: "mlp.down",
    rule: "GGUF: ffn_down",
  },

  // State-space norms. The projections are `MIXER_PARTS`' business, but a
  // `ssm_norm` is a norm on any architecture.
  { pattern: /\bssm_norm\b/, role: "norm", rule: "GGUF: ssm_norm" },

  /* -- llama.cpp's speech models, which use a second convention -------------
   *
   * Whisper and the conformer ASR models do **not** use the flattened
   * underscore leaf above. They use dotted module paths under `enc.` and `dec.`
   * roots — `enc.blocks.7.attn.q`, `dec.blocks.7.cross_attn.v` — so none of the
   * rules above can see them, and both models measured at 54% and 2% of bytes
   * named before these rows existed.
   *
   * Read off the two repos rather than from llama.cpp's source, and deliberately
   * partial: what is here is what an unambiguous name states. The conformer's
   * convolution module and the RNNT prediction/joint networks are left in
   * `other` on purpose — see plan §19c for the measured remainder.
   *
   * Every pattern allows a trailing `.weight`/`.bias`, and the first version did
   * not. Classification runs on the **physical** name, which carries the
   * parameter suffix; only the logical name has it stripped. Anchoring on `$`
   * therefore matched nothing whatsoever — and the only reason it was caught is
   * that the two speech models came back byte-for-byte unchanged after the rules
   * landed. A rule that matches nothing is invisible in every way except the
   * measurement.
   */
  {
    pattern: /(?:^|\.)(?:self_|cross_)?attn\.(?:linear_)?q(?:\.(?:weight|bias))?$/,
    role: "attn.q",
    rule: "GGUF speech: attn.q",
  },
  {
    pattern: /(?:^|\.)(?:self_|cross_)?attn\.(?:linear_)?k(?:\.(?:weight|bias))?$/,
    role: "attn.k",
    rule: "GGUF speech: attn.k",
  },
  {
    pattern: /(?:^|\.)(?:self_|cross_)?attn\.(?:linear_)?v(?:\.(?:weight|bias))?$/,
    role: "attn.v",
    rule: "GGUF speech: attn.v",
  },
  {
    pattern: /(?:^|\.)(?:self_|cross_)?attn\.(?:linear_out|out)(?:\.(?:weight|bias))?$/,
    role: "attn.o",
    rule: "GGUF speech: attn.out",
  },
  /*
   * The conformer's macaron feed-forward. `linear1` expands and `linear2`
   * contracts, which is what the pair means in every conformer implementation
   * and is checkable against the shapes rather than taken on faith — the two are
   * transposes of each other. Anchored on `ff1.`/`ff2.` so this cannot reach a
   * `linear1` somewhere else in a file.
   */
  {
    pattern: /\bff[12]\.linear1(?:\.(?:weight|bias))?$/,
    role: "mlp.up",
    rule: "GGUF speech: ffN.linear1",
  },
  {
    pattern: /\bff[12]\.linear2(?:\.(?:weight|bias))?$/,
    role: "mlp.down",
    rule: "GGUF speech: ffN.linear2",
  },
  { pattern: /\bconv\.bn\b/, role: "norm", rule: "GGUF speech: conv.bn" },
  /*
   * The RNNT transducer: one aggregate row, not a family.
   *
   * Reviewer ruling (2026-08-09). At ~3% of one model's bytes on a
   * first-of-kind architecture, a `pred.*`/`joint.*` row set would be growth by
   * resemblance — a taxonomy invented for a single model. Declining to name them
   * at all would be the confidently-withholding error. One row saying what the
   * thing is, with the hover carrying exact tensors, is the proportionate
   * answer; review splits it if ASR becomes a corpus segment.
   */
  {
    pattern:
      /(?:^|\.)(?:pred\.lstm\.\d+\.W[xh]|joint\.(?:enc|pred|out))(?:\.(?:weight|bias))?$/,
    role: "transducer",
    rule: "GGUF speech: RNNT pred/joint",
  },
  {
    pattern: /(?:^|\.)(?:enc|dec)\.pos_emb(?:\.(?:weight|bias))?$/,
    role: "embed",
    rule: "GGUF speech: pos_emb",
  },

  /* -- BERT / RoBERTa / XLM-R, and everything that copied them ------------- */
  { pattern: /attention\.self\.query\b/, role: "attn.q", rule: "attention.self.query" },
  { pattern: /attention\.self\.key\b/, role: "attn.k", rule: "attention.self.key" },
  { pattern: /attention\.self\.value\b/, role: "attn.v", rule: "attention.self.value" },
  // ViT spells the same three with the segment doubled, not `self`.
  {
    pattern: /attention\.attention\.query\b/,
    role: "attn.q",
    rule: "attention.attention.query (ViT)",
  },
  {
    pattern: /attention\.attention\.key\b/,
    role: "attn.k",
    rule: "attention.attention.key (ViT)",
  },
  {
    pattern: /attention\.attention\.value\b/,
    role: "attn.v",
    rule: "attention.attention.value (ViT)",
  },
  {
    pattern: /attention\.output\.dense\b/,
    role: "attn.o",
    rule: "attention.output.dense",
  },
  {
    pattern: /(?:^|\.)intermediate\.dense\b/,
    role: "mlp.up",
    rule: "intermediate.dense",
  },
  /*
   * The trap this table exists to survive.
   *
   * BERT and ViT use `.output.dense` for **both** the attention output
   * projection (`layer.0.attention.output.dense`) and the FFN down-projection
   * (`layer.0.output.dense`). A naive pair matches the attention tensor twice
   * and disagrees — and under abstention that means `attn.o` goes *unnamed* on
   * every BERT and ViT, which is worse than the bug being fixed.
   *
   * The lookbehind must sit **after** the separator. One survey reported
   * writing it before, where it silently matched `attention.output.dense`
   * anyway. The two rows are now disjoint by construction rather than by table
   * order, so they stay correct under any resolver policy.
   */
  {
    pattern: /(?:^|\.)(?<!attention\.)output\.dense\b/,
    role: "mlp.down",
    rule: "block-level output.dense",
  },

  /* -- DistilBERT, which shares nothing but the idea ----------------------- */
  { pattern: /attention\.q_lin\b/, role: "attn.q", rule: "DistilBERT q_lin" },
  { pattern: /attention\.k_lin\b/, role: "attn.k", rule: "DistilBERT k_lin" },
  { pattern: /attention\.v_lin\b/, role: "attn.v", rule: "DistilBERT v_lin" },
  { pattern: /attention\.out_lin\b/, role: "attn.o", rule: "DistilBERT out_lin" },
  { pattern: /\bffn\.lin1\b/, role: "mlp.up", rule: "DistilBERT ffn.lin1" },
  { pattern: /\bffn\.lin2\b/, role: "mlp.down", rule: "DistilBERT ffn.lin2" },

  /* -- timm / DINOv2 vision transformers ----------------------------------- */
  { pattern: /\battn\.qkv\b/, role: "attn.qkv", rule: "fused QKV (timm/DINOv2)" },
  { pattern: /\battn\.proj\b/, role: "attn.o", rule: "attn.proj (timm/DINOv2)" },
  { pattern: /patch_embed(?:ding|dings)?\./, role: "embed", rule: "patch embedding" },

  /* -- T5 and its descendants ---------------------------------------------- */
  {
    pattern: /(?:^|\.)(?:SelfAttention|self_attention|EncDecAttention)\.q\./,
    role: "attn.q",
    rule: "T5 attention q",
  },
  {
    pattern: /(?:^|\.)(?:SelfAttention|self_attention|EncDecAttention)\.k\./,
    role: "attn.k",
    rule: "T5 attention k",
  },
  {
    pattern: /(?:^|\.)(?:SelfAttention|self_attention|EncDecAttention)\.v\./,
    role: "attn.v",
    rule: "T5 attention v",
  },
  {
    pattern: /(?:^|\.)(?:SelfAttention|self_attention|EncDecAttention)\.o\./,
    role: "attn.o",
    rule: "T5 attention o",
  },
  // `wi`/`wo` must stay qualified: several TTS checkpoints spell the attention
  // *output* projection `attention.wo`, and an unqualified row would file it as
  // a feed-forward down-projection.
  {
    pattern: /(?:^|\.)(?:DenseReluDense|mlp)\.wi_0\./,
    role: "mlp.gate",
    rule: "T5 v1.1 gated FF, activated branch",
  },
  {
    pattern: /(?:^|\.)(?:DenseReluDense|mlp)\.wi_1\./,
    role: "mlp.up",
    rule: "T5 v1.1 gated FF, linear branch",
  },
  {
    pattern: /(?:^|\.)(?:DenseReluDense|mlp)\.wi\./,
    role: "mlp.up",
    rule: "T5 FF input projection",
  },
  {
    pattern: /(?:^|\.)(?:DenseReluDense|mlp)\.wo\./,
    role: "mlp.down",
    rule: "T5 FF output projection",
  },
  {
    pattern: /relative_attention_bias/,
    role: "pos-embed",
    rule: "T5 relative position bias",
  },
  { pattern: /(?:^|\.)shared\.weight$/, role: "embed", rule: "shared embedding table" },

  /* -- BART / Whisper / CLIP ------------------------------------------------ */
  /*
   * `fc1`/`fc2` are bare in Whisper and BART, so the row cannot require an
   * `mlp.` parent — but timm's squeeze-excite blocks also spell their channel
   * gate `se.fc1`/`se.fc2`, and those are not a feed-forward at all. The
   * lookbehind declines exactly that, which is the same mislabelling rejected
   * for mobilenetv3, arriving through a regex instead of a judgement.
   */
  { pattern: /(?:^|\.)(?<!se\.)fc1\./, role: "mlp.up", rule: "fc1 (FFN up)" },
  { pattern: /(?:^|\.)(?<!se\.)fc2\./, role: "mlp.down", rule: "fc2 (FFN down)" },
  /*
   * Scoped to the attention parents rather than a bare `\bout_proj\b`.
   *
   * The bare form is safe against SSM mixers only because `signatureResolver`
   * runs first — a correctness argument resting on resolver order. It would
   * also file `bge-reranker-v2-m3`'s `classifier.out_proj` as `attn.o`, which
   * is a scoring head and not attention at all. `_` is a word character, so
   * `(?:^|\.)attn\.` correctly declines `self_attn`, `linear_attn` and
   * `mixer.out_proj` alike; the alternation names the ones we mean.
   *
   * This also finally covers the `\bo_proj\b` ≠ `out_proj` underscore trap.
   */
  {
    pattern: /(?:^|\.)(?:self_attn|encoder_attn|attn)\.out_proj\b/,
    role: "attn.o",
    rule: "attention out_proj",
  },
  { pattern: /(?:^|\.)embed_positions\./, role: "pos-embed", rule: "embed_positions" },
  {
    pattern: /\bpre_layrnorm\b/,
    role: "norm",
    rule: "CLIP pre_layrnorm (upstream typo)",
  },

  /* -- nomic_bert ----------------------------------------------------------- */
  { pattern: /(?:^|\.)attn\.Wqkv\./, role: "attn.qkv", rule: "Wqkv (fused QKV)" },
  { pattern: /(?:^|\.)emb_ln\./, role: "norm", rule: "emb_ln" },

  /* -- DeepSeek / MLA lineage ----------------------------------------------- */
  /*
   * Verified against 1 571 421 distinct tensor names in the cached corpus
   * before being written: `attn.wo_[ab]` and `attn.wkv` occur only under
   * `layers.`/`mtp.` in this lineage, and the two bare names below are single
   * exact hits in the whole corpus.
   *
   * These are vocabulary, not a family table. `wo_a`/`wo_b` are *not* a
   * low-rank pair — their shared dimension is larger than the hidden size — so
   * the shape resolver correctly declines them, and the evidence that they are
   * the output projection is their name rather than their arithmetic. That is
   * exactly what this layer is for.
   */
  { pattern: /(?:^|\.)attn\.wo_[ab]\b/, role: "attn.o", rule: "attn.wo_a / wo_b" },
  {
    pattern: /(?:^|\.)attn\.(?:compressor\.)?wkv\b/,
    role: "attn.kv",
    rule: "compressed KV projection",
  },
  /*
   * A model whose embedding table is called `embed` and whose head is called
   * `head` had 2 GiB — a third of its unnamed mass, and its two single largest
   * tensors — sitting in `other` because every existing rule spells them
   * `embed_tokens` and `lm_head`. Anchored to the whole name: bare `head` is
   * far too generic as a substring.
   */
  { pattern: /(?:^|\.)embed\.weight$/, role: "embed", rule: "embed (bare)" },
  { pattern: /^head\.weight$/, role: "lm_head", rule: "head (bare)" },

  /* -- fused feed-forward, MoE and dense ------------------------------------ */
  /*
   * The largest single gap in the corpus: ~2459 GiB unnamed across the cached
   * repos, and 100% of `thinkingmachines/Inkling`'s bytes.
   *
   * **Fusion proved by arithmetic, not by naming.** In every case
   * `out(gate_up) == 2 x in(down)`, which the two sibling tensors establish
   * without reading config at all — a separate gate would be `[I, H]`, and a
   * fused one is `[2I, H]`. Verified across ten repos.
   *
   * **The suffixes are spelled out because `\b` will not do it.** `\bw2\b`
   * misses `w2_weight`, `\bdown_proj\b` misses `down_proj_blocks`, and
   * `\bbias\b` misses `gate_up_proj_bias` — all the same underscore trap this
   * file has now been bitten by six times. That is the whole reason a rule for
   * `w2` and `down_proj` already existed and yet 93.5% of `gpt-oss-120b` was
   * unnamed.
   *
   * **Scoped to an `ffn`/`mlp`/`experts` parent on evidence.** A bare `gate_up`
   * row would claim `Aratako/Irodori-TTS-v4-Small`'s
   * `blocks.N.attention_adaln.gate_up` — adaLN modulation, where "gate" is the
   * parameter being produced rather than half of a fused pair, sitting beside
   * `gate_down`/`scale_up`/`shift_up` and with the real FFN elsewhere. Measured:
   * that repo recovers 0.00 GiB, which is the correct outcome.
   */
  {
    pattern:
      /(?:^|\.)(?:(?:ffn|mlp|experts)\.gate_up|gate_up_proj)(?:_(?:blocks|scales|bias))?(?:[._]|$)/,
    role: "mlp.gate_up",
    rule: "gate_up_proj (fused gate+up)",
  },
  {
    pattern: /(?:^|[._])(?:shared_)?w13(?:_[a-z]+)?(?:[._]|$)/,
    role: "mlp.gate_up",
    rule: "w13 (fused w1+w3)",
  },
  {
    pattern: /(?:^|[._])(?:shared_)?w2_(?:weight|md)(?:[._]|$)/,
    role: "mlp.down",
    rule: "w2_weight / w2_md (down-projection)",
  },
  {
    pattern: /(?:^|\.)down_proj_(?:blocks|scales|bias)(?:[._]|$)/,
    role: "mlp.down",
    rule: "down_proj_blocks / _scales / _bias",
  },
  {
    pattern: /(?:^|\.)qkv_proj(?:[._]|$)/,
    role: "attn.qkv",
    rule: "qkv_proj (fused QKV)",
  },

  /* -- Megatron, Conformer and other module-native spellings ---------------- */
  { pattern: /(?:^|[._])linear_fc1\./, role: "mlp.up", rule: "Megatron linear_fc1" },
  { pattern: /(?:^|[._])linear_fc2\./, role: "mlp.down", rule: "Megatron linear_fc2" },
  // ESPnet/NeMo `MultiHeadedAttention` names its own members; an identity
  // rather than an inference, corroborated by all four being [1024,1024].
  {
    pattern: /(?:^|\.)self_attn\.linear_q\./,
    role: "attn.q",
    rule: "Conformer linear_q",
  },
  {
    pattern: /(?:^|\.)self_attn\.linear_k\./,
    role: "attn.k",
    rule: "Conformer linear_k",
  },
  {
    pattern: /(?:^|\.)self_attn\.linear_v\./,
    role: "attn.v",
    rule: "Conformer linear_v",
  },
  {
    pattern: /(?:^|\.)self_attn\.linear_out\./,
    role: "attn.o",
    rule: "Conformer linear_out",
  },
  {
    pattern: /(?:^|\.)feed_forward\d*\.linear1\./,
    role: "mlp.up",
    rule: "Conformer macaron FF input",
  },
  {
    pattern: /(?:^|\.)feed_forward\d*\.linear2\./,
    role: "mlp.down",
    rule: "Conformer macaron FF output",
  },
  { pattern: /(?:^|\.)[wW]qkv\./, role: "attn.qkv", rule: "fused QKV (wqkv)" },
  /*
   * The lookbehinds sit AFTER the separator, on the `output.dense` precedent.
   * A bare `wo` row would collide with T5's `DenseReluDense.wo` -> `mlp.down`,
   * and abstention would strip the whole T5 family of its FF output naming.
   */
  {
    pattern: /(?:^|\.)(?<!DenseReluDense\.)(?<!mlp\.)wo\.(?:weight|bias)$/,
    role: "attn.o",
    rule: "attention wo (output projection)",
  },
  /*
   * `pos_embed`, not `pos_emb`. The shorter form also matches
   * `patch_embed.pos_emb.weight`, which the patch-embedding row already claims
   * — two rows, two roles, and the resolver would abstain, turning a named
   * tensor into an unnamed one.
   */
  { pattern: /(?:^|\.)pos_embed(?:ding)?\b/, role: "pos-embed", rule: "pos_embed" },
  // Inkling's `_<in><out>` suffixes, corroborated arithmetically: `wq_du` and
  // `wo_ud` are both [4096,4096], `wk_dv`/`wv_dv` share the GQA kv width.
  // `[a-z]{2}` exactly, so DeepSeek's `wo_a`/`wo_b` are untouched.
  { pattern: /(?:^|\.)attn\.wq_[a-z]{2}\b/, role: "attn.q", rule: "wq_<dims>" },
  { pattern: /(?:^|\.)attn\.wk_[a-z]{2}\b/, role: "attn.k", rule: "wk_<dims>" },
  { pattern: /(?:^|\.)attn\.wv_[a-z]{2}\b/, role: "attn.v", rule: "wv_<dims>" },
  { pattern: /(?:^|\.)attn\.wo_[a-z]{2}\b/, role: "attn.o", rule: "wo_<dims>" },
  { pattern: /(?:^|\.)mlp\.w2_[a-z]{2}\./, role: "mlp.down", rule: "w2_<dims>" },
  {
    pattern: /(?:^|\.)(?:self_attn|attention|attn)\.g_proj\b/,
    role: "attn.gate",
    rule: "attention output gate",
  },
  { pattern: /(?:^|\.)unembed\.weight$/, role: "lm_head", rule: "unembed" },

  /* -- masked-language-model heads ------------------------------------------ */
  // `lm_head` means a projection onto the vocabulary, and these are exactly
  // that. Task heads — `classifier`, `pooler`, `seq_relationship` — are not,
  // and are deliberately left unnamed rather than borrowing the role.
  {
    pattern: /(?:^|\.)cls\.predictions\.transform\.dense\b/,
    role: "lm_head",
    rule: "BERT MLM head transform",
  },
  { pattern: /\bvocab_transform\b/, role: "lm_head", rule: "DistilBERT MLM transform" },
  {
    pattern: /\bvocab_projector\b/,
    role: "lm_head",
    rule: "DistilBERT MLM projection",
  },
];

/**
 * Vocabulary aliasing, abstaining where a name is ambiguous.
 *
 * The reviewer's guard: a synonym is an identity, not an inference — *but only
 * while the match is unique*. Two rows matching one tensor means the vocabulary
 * genuinely collides there, and the honest answer is to say nothing and let a
 * later resolver, or `other`, have it. Encoding the abstention is what lets
 * this layer carry no confidence tier without lying.
 */
const synonymResolver: Resolver = {
  tier: "synonym",
  resolve(block) {
    const out = new Map<string, { role: string; rule: string }>();
    for (const tensor of block.tensors) {
      const hits = SYNONYM_RULES.filter((r) => r.pattern.test(tensor.name));
      // Distinct *roles*, not distinct rules: two rows agreeing on the answer
      // is not an ambiguity, it is a redundant table.
      if (new Set(hits.map((h) => h.role)).size !== 1) continue;
      const [first] = hits;
      if (first) out.set(tensor.name, { role: first.role, rule: first.rule });
    }
    return out;
  },
};

/**
 * Signature first, then vocabulary.
 *
 * `signatureResolver` keeps its head start so that a gated-linear layer's
 * `out_proj` is claimed as `mixer.out` before any attention row sees it —
 * exactly as it was before the synonym table existed. The new rows then outrank
 * `ROLE_RULES`, which is what moves a `…dense.bias` from the generic `bias`
 * catch-all into the component it belongs to.
 */
/**
 * Layer 2: find a projection that was factored into two tensors, from shapes
 * alone.
 *
 * ## Why this cannot be a name rule
 *
 * `deepseek-ai/DeepSeek-V4-Flash-0731` reported **95.1% of its bytes named and
 * zero attention rows** — its projections are factored, so the whole attention
 * mechanism is too small to dent a byte count while being entirely absent from
 * the map. No name rule reaches it: `wq_a`/`wq_b` match nothing, and writing
 * rows for them would fix one checkpoint. Shapes are the general instrument.
 *
 * ## What "factored" means here, measured rather than assumed
 *
 * Two 2-D tensors A `[r, d]` and B `[n, r]` where the inner `r` is **strictly
 * smaller than both outer dimensions**: a bottleneck that one dense
 * `[n, d]` matrix would not have. That is a fact about the arithmetic, not
 * about the naming.
 *
 * The reviewer commissioned this expecting it to name all four of V4-Flash's
 * projections. Checked against the real headers, it names **one**, and the
 * measurement is the reason (`docs/evidence/shape-resolver-prediction.md`):
 *
 * - `wq_a [1024,4096]` + `wq_b [32768,1024]` — inner 1024 < 4096. A bottleneck.
 * - `wo_a [8192,4096]` + `wo_b [4096,8192]` — inner **8192 > 4096**. That is an
 *   expansion through 64 heads x 128 and back, not a low-rank factorization,
 *   and calling it one would be a claim about the architecture that is false.
 * - `wkv [512,4096]` — a single compression tensor with no partner at all.
 *
 * So the rule declines two of three on purpose. Naming them is layer 1's job,
 * from `model_type`, where the evidence is the architecture's own declaration
 * rather than a shape that does not support the claim.
 *
 * ## Position, and when it may be claimed
 *
 * Shape says a pair exists; it cannot say Q from O. Per the reviewer, position
 * comes from config *arithmetic* where the solution is unique — the same
 * identifiability move already used for bits x group-size. A pair whose inner
 * dimension equals `q_lora_rank` **and** whose fan-out equals
 * `heads x head_dim` is the Q pair and nothing else. Where that does not
 * resolve, the pair stays `attn.lowrank`: unpositioned, visibly present, and
 * not guessed at.
 */
/**
 * Quantization sidecars, which are not projections and must not be paired.
 *
 * Measured, not anticipated: on `DeepSeek-V4-Flash-0731` the block scales
 * `wq_a.scale [8,32]` and `wq_b.scale [256,8]` are themselves a valid factored
 * pair — a *shadow* of the real one at 1/128 resolution, since a per-block
 * scale plane has the shape of the weight it scales, divided down. The
 * resolver found it, could not match rank 8 to any config anchor, and emitted
 * a second `attn.lowrank` row beside the correctly-named `attn.q`.
 *
 * The row was not wrong about the arithmetic. It was wrong about what a
 * projection is: a sidecar describes a weight rather than being one, and
 * naming the same factorization twice would double-count it on every
 * block-quantized checkpoint — which is most frontier models now.
 *
 * `.scale` singular as well as `.scales`: this repo uses the former and
 * `PART_SUFFIXES` only knew the latter.
 */
const SIDECAR_SUFFIX = /\.(?:scales?|biases|zeros|zero_points|qzeros|g_idx)$/;

/**
 * The module a tensor belongs to — its path with the parameter suffix and the
 * leaf name both removed.
 *
 * `layers.3.attn.wq_a.weight` -> `layers.3.attn`, so `wq_a` and `wq_b` are
 * siblings; `mtp.0.attn.wo_b.weight` -> `mtp.0.attn`, so it is not a sibling of
 * anything under `mtp.1`.
 */
function moduleOf(name: string): string {
  const base = name.replace(/\.weight$/, "").replace(SIDECAR_SUFFIX, "");
  const cut = base.lastIndexOf(".");
  return cut === -1 ? "" : base.slice(0, cut);
}

const lowRankResolver: Resolver = {
  tier: "shape",
  resolve(block) {
    const out = new Map<string, { role: string; rule: string }>();
    // Only inside an attention module, and only over 2-D weights. Scoping is
    // not tidiness: an MoE block holds thousands of expert tensors that share
    // dimensions with one another, and an unscoped pairwise scan would both
    // invent pairs and go quadratic on a 35 000-tensor checkpoint.
    const candidates = block.tensors.filter(
      (t) =>
        t.shape.length === 2 &&
        /(?:^|[._])attn(?:ention)?[._]/.test(t.name) &&
        !SIDECAR_SUFFIX.test(t.name),
    );
    if (candidates.length < 2 || candidates.length > 32) return out;

    for (const a of candidates) {
      for (const b of candidates) {
        if (a.name >= b.name) continue;
        const [aOut, aIn] = a.shape as [number, number];
        const [bOut, bIn] = b.shape as [number, number];
        // A feeds B: A's output is B's input, and that shared dimension is a
        // bottleneck relative to what enters and leaves the pair.
        if (aOut !== bIn) continue;
        const rank = aOut;
        if (rank >= aIn || rank >= bOut) continue;
        // Both halves must live in the same module. The global pseudo-block is
        // a bag of everything outside the layer stack, not one module: on
        // V4-Flash it paired `mtp.0.attn.wo_b` with `mtp.1.attn.wo_a` — two
        // different multi-token-prediction heads whose dimensions happened to
        // meet. A factored projection's halves are siblings; anything else is
        // arithmetic coincidence.
        if (moduleOf(a.name) !== moduleOf(b.name)) continue;

        const positioned = positionOf(block, rank, bOut);
        const role = positioned ?? "attn.lowrank";
        const rule = positioned
          ? `factored pair, rank ${rank}, matched to config`
          : `factored pair, rank ${rank} (position not determined)`;
        if (!out.has(a.name)) out.set(a.name, { role, rule });
        if (!out.has(b.name)) out.set(b.name, { role, rule });
      }
    }
    return out;
  },
};

/**
 * Which projection a factored pair is, when the config leaves exactly one
 * answer — and `undefined` the moment it leaves two.
 */
function positionOf(
  block: LayerBlock,
  rank: number,
  fanOut: number,
): string | undefined {
  const anchors = block.anchors;
  if (!anchors) return undefined;
  const { qLoraRank, kvLoraRank, heads, headDim } = anchors;

  /*
   * Two conditions, and both were weaker than this comment used to claim.
   *
   * **The fan-out must equal `heads * headDim`, not merely divide by `heads`.**
   * The old test was `fanOut % heads === 0`, which almost every fan-out in a
   * transformer satisfies — head counts are small powers of two and widths are
   * multiples of them. It reads like an identity and behaves like a formality.
   *
   * **And the rank must name exactly one position.** The old code compared
   * against `qLoraRank` alone, so any rank equal to it was Q. On
   * `DeepSeek-V4-Flash` — the model this resolver was written for —
   * `q_lora_rank === kv_lora_rank === 1024`, so a KV or O pair factoring at
   * 1024 would have been *named* `attn.q`. It escaped only because that repo's
   * other pair happens to factor at 8192 rather than at its config's 1024.
   *
   * A mislabelled cell is worse than an unnamed one, so an ambiguous rank
   * abstains and the caller falls back to `attn.lowrank`.
   */
  if (heads === undefined) return undefined;
  /*
   * Declared, or derived from two other declared numbers — never guessed.
   * `head_dim` is frequently omitted because it is conventionally
   * `hidden_size / num_attention_heads`; deriving it from those two is reading
   * the config, not inferring from shapes. If neither route yields a width,
   * there is nothing to check the fan-out against and the pair abstains.
   */
  const perHead =
    headDim ??
    (anchors.hiddenSize !== undefined && anchors.hiddenSize % heads === 0
      ? anchors.hiddenSize / heads
      : undefined);
  if (perHead === undefined) return undefined;
  if (fanOut !== heads * perHead) return undefined;

  const namesQ = qLoraRank !== undefined && rank === qLoraRank;
  const namesKv = kvLoraRank !== undefined && rank === kvLoraRank;
  // Ambiguous: this rank is consistent with more than one declared position, so
  // it identifies none of them.
  if (namesQ && namesKv) return undefined;
  if (namesQ) return "attn.q";
  return undefined;
}

/**
 * A tensor's *stem*: its name with the sidecar and parameter suffixes removed.
 *
 * `block.0.mlp.mlp1_weight.blocks` -> `block.0.mlp.mlp1`
 * `block.0.mlp.mlp1_bias`          -> `block.0.mlp.mlp1`
 *
 * So the packed payload, its scale plane and its bias all name one projection,
 * and the resolver below reasons about projections rather than about files.
 */
function ffnStem(name: string): string {
  return name
    .replace(SIDECAR_SUFFIX, "")
    .replace(/[._](?:blocks|scales?|weight|bias)$/, "")
    .replace(/_(?:weight|bias)$/, "");
}

/**
 * The fused gate+up projection and its down partner, named by arithmetic.
 *
 * ## Why this exists when a name table already covers `gate_up_proj`
 *
 * `gpt-oss`'s `original/model.safetensors` spells the same two projections
 * `mlp.mlp1_weight` and `mlp.mlp2_weight`. Nothing in the name says gate, up or
 * down — the numbering is positional — so every name rule declined and **90.7%
 * of that checkpoint's bytes landed in `other`** while the parameter count was
 * correct. A visibly empty map above an honest number.
 *
 * The obvious fix is a synonym row for `mlp1`/`mlp2`. The reviewer's ruling
 * (2026-08-15) is that where the *shape* settles a role, the shape tier should
 * settle it: a synonym row would name this one checkpoint, and the arithmetic
 * names the pattern. It is the role-axis counterpart of what plan §24 proposes
 * for formats, and it is deliberately **not** §24 — that signature identifies a
 * quantization format and would call every one of these tensors MXFP4 while
 * still not knowing which is the gate.
 *
 * ## The arithmetic, and exactly how far it reaches
 *
 * The name table above already states it: a separate gate is `[I, H]` and a
 * fused one is `[2I, H]`, so `out(gate_up) == 2 x in(down)`. Two siblings
 * establish that without reading config.
 *
 * This resolver claims the case it can prove from packed shapes alone: **two
 * sibling projections identical on every axis but one, where that axis is
 * exactly twice as large in one of them.** The larger is the fused gate+up, the
 * smaller is down.
 *
 * That is narrower than the general rule and the narrowness is the point.
 * Requiring the other axes to match means both halves share an input width,
 * which holds when intermediate == hidden — true of `gpt-oss`, not true
 * everywhere. Where it does not hold, the trailing axes differ and this
 * declines, because the packed shape hides the element count needed to compare
 * `in(down)` against `out(gate_up)` directly and inferring it would mean
 * assuming a bit width. A pair this cannot prove stays `other`: visibly
 * unplaced, which is the honest outcome, rather than confidently mislabelled.
 *
 * Scoped to a feed-forward parent for the reason the name rules are: a bare
 * 2:1 shape relation is common, and `attention_adaln.gate_up` is a real tensor
 * that means something else entirely.
 */
const fusedFeedForwardResolver: Resolver = {
  tier: "shape",
  resolve(block) {
    const out = new Map<string, { role: string; rule: string }>();

    /** Representative weight shape per projection stem, and its tensors. */
    const stems = new Map<
      string,
      { shape: readonly number[] | undefined; names: string[] }
    >();

    for (const tensor of block.tensors) {
      if (!/(?:^|[._])(?:ffn|mlp|experts)[._]/.test(tensor.name)) continue;
      const stem = ffnStem(tensor.name);
      const entry = stems.get(stem) ?? { shape: undefined, names: [] };
      entry.names.push(tensor.name);
      // The payload carries the shape that matters; a bias is one-dimensional
      // and a scale plane is the payload with an axis dropped, so neither can
      // establish the relation on its own.
      if (/(?:[._]blocks|[._]weight)$/.test(tensor.name) && tensor.shape.length >= 2) {
        entry.shape = tensor.shape;
      }
      stems.set(stem, entry);
    }

    type Sized = [string, { shape: readonly number[]; names: string[] }];
    const sized = [...stems].filter(([, e]) => e.shape !== undefined) as Sized[];
    if (sized.length < 2) return out;
    // Bound the pairwise scan. An MoE block holds a lot of tensors and this is
    // the same reason `lowRankResolver` caps its candidate list.
    if (sized.length > 16) return out;

    /*
     * The pair is found by search, not by assuming the module holds only two
     * projections — which it does not.
     *
     * That assumption was the first version of this code and it declined on the
     * very model it was written for. `gpt-oss`'s expert module carries a third
     * shaped tensor, the MoE router `mlp.gate.weight [32, 2880]`, so an
     * "exactly two candidates" test never reached the arithmetic. It was caught
     * on the live site rather than in this file, because the unit fixture had
     * been written with only the two projections in it — the tidier module is
     * the one that does not exist.
     *
     * **Uniqueness is what replaces it.** Every candidate pair is checked and
     * the claim is made only if exactly one satisfies the relation. A second
     * qualifying pair means the module is ambiguous and the resolver declines,
     * which preserves the property the count was there for without inheriting
     * its blindness to a third tensor that is not a projection at all.
     */
    let match: { fused: Sized; down: Sized } | undefined;
    for (let i = 0; i < sized.length; i += 1) {
      for (let j = i + 1; j < sized.length; j += 1) {
        const first = sized[i] as Sized;
        const second = sized[j] as Sized;
        const a = first[1].shape;
        const b = second[1].shape;
        if (a.length !== b.length) continue;

        let differing = -1;
        let mismatched = false;
        for (let axis = 0; axis < a.length; axis += 1) {
          if (a[axis] === b[axis]) continue;
          if (differing !== -1) {
            mismatched = true;
            break;
          }
          differing = axis;
        }
        // More than one axis differs, or none does: nothing is proved either way.
        if (mismatched || differing === -1) continue;

        const aAxis = a[differing] as number;
        const bAxis = b[differing] as number;
        const fused =
          aAxis === bAxis * 2 ? first : bAxis === aAxis * 2 ? second : undefined;
        if (fused === undefined) continue;

        // A second qualifying pair makes the module ambiguous. Decline rather
        // than pick, which is the whole posture of this tier.
        if (match !== undefined) return out;
        match = { fused, down: fused === first ? second : first };
      }
    }
    if (match === undefined) return out;

    const rule = "fused gate+up by shape (2:1 against its down partner)";
    for (const name of match.fused[1].names) {
      out.set(name, { role: "mlp.gate_up", rule });
    }
    for (const name of match.down[1].names) out.set(name, { role: "mlp.down", rule });
    return out;
  },
};

const RESOLVERS: readonly Resolver[] = [
  signatureResolver,
  /*
   * Before both name tables, and the ordering is load-bearing in one direction:
   * `embed_tokens_per_layer` matches `/embed_tokens/` in `ROLE_RULES`, so if the
   * vocabulary got there first the PLE table would keep being counted as the
   * ordinary embedding — named, and wrong in the way that made the GGUF and the
   * safetensors disagree about the same model.
   */
  pleResolver,
  // Before the synonym table, which reads a bare `fc1` as the up projection.
  zeroIndexedFfnResolver,
  synonymResolver,
  vocabularyResolver,
  // Last: a shape claim is the weakest evidence in the pipeline and may only
  // speak for a tensor no name could place.
  lowRankResolver,
  fusedFeedForwardResolver,
];

/**
 * Place every tensor in one block.
 *
 * Never throws and never drops: an unfamiliar architecture still gets a grid,
 * with whatever nothing could name sitting visibly in `other`.
 */
export function classifyLayer(block: LayerBlock): Map<string, Classification> {
  const placed = new Map<string, Classification>();
  for (const resolver of RESOLVERS) {
    for (const [name, placement] of resolver.resolve(block)) {
      // First confident answer wins; a later resolver may not overwrite it.
      if (!placed.has(name)) placed.set(name, { ...placement, tier: resolver.tier });
    }
  }
  for (const tensor of block.tensors) {
    if (!placed.has(tensor.name)) {
      placed.set(tensor.name, {
        role: "other",
        rule: "no rule matched",
        tier: "shape",
      });
    }
  }
  return placed;
}

/**
 * Classify one name in isolation.
 *
 * A **name-only** path, kept for callers that have a name and nothing else. It
 * builds a single-tensor block, so it can reach the vocabulary table and
 * nothing that needs context: no sibling signature, and — once it exists — no
 * shape reasoning. Anything that has the tensors should call `classifyLayer`.
 */
export function classifyTensor(
  name: string,
  siblings?: ReadonlySet<string>,
  family?: string,
): TensorPlacement {
  const { layer } = layerGroupOf(name);
  // A name carrying no measurements, which is precisely what this path has.
  // Built in full rather than cast, so that adding a field to `InventoryTensor`
  // is a compile error here instead of a silent `undefined` reaching a resolver.
  const stand = (n: string): InventoryTensor => ({
    name: n,
    dtype: "",
    shape: [],
    dataOffsets: [0, 0],
    numel: 0,
    byteLength: 0,
    file: "",
    absoluteStart: 0,
    absoluteEnd: 0,
  });
  const block: LayerBlock = {
    layer,
    tensors: [
      stand(name),
      ...[...(siblings ?? [])].filter((n) => n !== name).map(stand),
    ],
    family,
    // The name-only path carries no shapes, so the shape resolver has nothing
    // to work with here by construction — not an omission.
    anchors: undefined,
  };
  const found = classifyLayer(block).get(name);
  return {
    layer,
    role: found?.role ?? "other",
    rule: found?.rule ?? "no rule matched",
  };
}

/**
 * A tensor as the model author thinks of it, which is not always how it is
 * stored.
 *
 * A quantized linear layer is three physical tensors — packed weight, scales,
 * biases — that together represent one logical weight matrix. Grouping them is
 * what makes effective bits-per-weight computable: bytes of all three, divided
 * by the parameter count of the *original* matrix.
 */
export interface LogicalTensor {
  /** Base name with any `.scales`/`.biases` suffix removed. */
  readonly name: string;
  readonly placement: TensorPlacement;
  /** Every physical tensor backing this one. */
  readonly parts: readonly InventoryTensor[];
  /**
   * The part that *is* the weights — `.weight`, or `.qweight` under AWQ/GPTQ.
   *
   * Stated rather than left for each consumer to work out. `parts[0]` is
   * whatever the header listed first, which for a GPTQ group is `g_idx`; the
   * map's click-through used exactly that and sent readers to the group-index
   * plane instead of the tensor they clicked on. Deriving "which part is the
   * real one" in more than one place is how the two answers drift.
   */
  readonly payloadName: string;
  readonly bytes: number;
  /**
   * Parameters in the logical matrix. For a plain tensor this is its element
   * count; for a quantized triple it is the count of the matrix *before*
   * packing, recovered from the scales plane.
   */
  readonly params: number;
  /** `bytes × 8 ÷ params` — the number the whole map is coloured by. */
  readonly effectiveBpw: number;
  /** Storage dtype of the main part, for the dtype-map fallback. */
  readonly dtype: string;
  /** `4@64` when a scheme was recognized *and resolved*. */
  readonly quantLabel: string | undefined;
  /** Set when config and shapes cannot both be true — surfaced, never smoothed. */
  readonly crossCheckError: string | undefined;
  /**
   * True when this is a quantized triple whose scheme could not be pinned down.
   *
   * The shapes alone constrain only the *product* `bits × groupSize`, so
   * `[4864,112]` packed with `[4864,14]` scales is equally consistent with
   * 8-bit/g32, 4-bit/g64 and 2-bit/g128 — three different models, all of which
   * unpack without error. When nothing resolves it, `params` and `effectiveBpw`
   * are not knowable, so they are reported as zero and the cell is marked
   * rather than given a confident number that is wrong by a factor of two.
   */
  readonly ambiguous: boolean;
  /**
   * Ambiguous because a **recognized** format was declined, not because the
   * evidence was insufficient to choose between candidates.
   *
   * The two share `ambiguous` — both refuse a parameter count and both report
   * no bits-per-weight — but they are different claims and a reader is owed the
   * right one. `renderAmbiguity` explains an unresolvable MLX triple with "the
   * stored shapes fix only the product of bits × group size", which is true of
   * that case and simply false of EXL3, where the shapes are unambiguous and
   * the reading is unverified. Without this flag, both notices appeared
   * together giving contradictory reasons for the same 400 tensors.
   */
  readonly declined: boolean;
  /**
   * This tensor is in the file but is **not a parameter** — a causal mask,
   * rotary frequencies, an `arange` of position ids.
   *
   * Set from `classifyBuffer`, which decides on name **and** shape and never
   * reads a byte. When true, `params` is 0 by construction: the elements are
   * real and stored, and they are not weights, so counting them answers a
   * different question from the one the page asks. `bytes` stays truthful — the
   * file really does contain them, and `anatomyTotals` keeps them in the byte
   * total for exactly that reason.
   *
   * Reported as `79b5cce5`: gpt2 read 137M against a real 124,439,808, the
   * difference being twelve `1×1×1024×1024` masks. See `analysis/buffers.ts`.
   */
  readonly isBuffer: boolean;
}

/**
 * Suffixes that mark the parts of one logical tensor.
 *
 * `.weight` is in the list, which is the non-obvious part: an MLX triple is
 * `<module>.weight` / `<module>.scales` / `<module>.biases`, so stripping only
 * the quantization suffixes would leave the packed weight in its own group and
 * every triple would silently fail to fold — each part counted separately, and
 * effective bits-per-weight computed as 32 for the packed plane.
 */
const PART_SUFFIXES = [
  // AWQ/GPTQ payload and its index sidecar. `.qzeros` and `.scales` were
  // already here, which was worse than nothing: the two sidecars grouped with
  // each other while `qweight` stayed alone reading 32 bits, so the map showed
  // a plausible pair of rows instead of one obviously wrong one.
  ".qweight",
  ".g_idx",
  ".scales",
  ".biases",
  ".zeros",
  ".zero_points",
  ".qzeros",
  /*
   * EXL3's four parts. Absent, each stood alone as its own logical tensor and
   * the `.trellis` payload reported its packed `I16` word count as a parameter
   * count — `malaiwah/Qwen3.8-27B-EXL3-K5K6-hydrated` read 10.8B at 16.00
   * bits/weight, which is the file's word count and the width of the word.
   *
   * Grouping them is what lets `findExl3Groups` see a module rather than four
   * unrelated arrays. It does not by itself produce a parameter count: EXL3 is
   * recognized and **refused**, not folded — see `analysis/exl3-trellis.ts` for
   * why the structural reading is shown as pending rather than asserted.
   */
  ".trellis",
  ".suh",
  ".svh",
  ".mcg",
  ".weight",
];

function baseName(name: string): string {
  for (const suffix of PART_SUFFIXES) {
    if (name.endsWith(suffix)) return name.slice(0, -suffix.length);
  }
  return name;
}

/**
 * Fold physical tensors into logical ones.
 *
 * `declared` carries `bits`/`group_size` from `config.json` when the repo states
 * them. It is genuinely needed rather than inferable, and the reason is sharper
 * than it first looks: the two observable widths give a single equation
 *
 *     32 · packedCols / scaleCols = bits × groupSize
 *
 * which constrains only the **product**. Real shapes `[4864, 112]` packed with
 * `[4864, 14]` scales are equally consistent with 8-bit/group 32, 4-bit/group 64
 * and 2-bit/group 128 — three different models, all of which unpack without
 * error. So a shape check alone cannot catch a wrong bits/group split, and
 * guessing one would scale the reported parameter count — and therefore the
 * headline bits-per-weight — by exactly the factor it got wrong.
 *
 * Resolution is therefore delegated to `mlx.ts`, which returns a candidate list
 * and commits only when the evidence picks one. Where it cannot, the cell is
 * marked `ambiguous` and reports no bpw at all rather than a confident number
 * that is wrong by a factor of two. Where the declaration contradicts the
 * shapes outright, `disagreement` says so and neither source wins.
 */
/**
 * Every tensor name, grouped by the layer it belongs to.
 *
 * Built once per inventory so `classifyTensor` can ask what else lives in a
 * tensor's layer without rescanning. Non-layer tensors share the `-1` bucket;
 * they have no layer siblings and no mixer rule should fire on them.
 */
function blocksByLayer(
  tensors: readonly InventoryTensor[],
  family: string | undefined,
  anchors: ArchitectureAnchors | undefined,
): Map<number, LayerBlock> {
  const byLayer = new Map<number, InventoryTensor[]>();
  for (const tensor of tensors) {
    const { layer } = layerGroupOf(tensor.name);
    // `-1` is the global pseudo-block: embeddings, the head, the final norm.
    // A real bucket rather than a special case, so tied and untied embeddings
    // fall out of the same mechanism every layer uses.
    const key = layer ?? -1;
    byLayer.set(key, [...(byLayer.get(key) ?? []), tensor]);
  }
  return new Map(
    [...byLayer].map(([key, group]) => [
      key,
      { layer: key === -1 ? undefined : key, tensors: group, family, anchors },
    ]),
  );
}

/**
 * Classify every block once, and index the result by tensor name.
 *
 * Once per inventory rather than once per tensor: a resolver that reasons over
 * a whole block would otherwise redo that work for every member of it, which is
 * quadratic on a 35 000-tensor MoE checkpoint.
 */
/*
 * Exported for `og/scripts/dump-names.ts`, the census instrument, which used to
 * call `classifyTensor` per name. That is the *name-only* path: it builds a
 * one-tensor block, so no signature resolver can ever fire and the census
 * reports every sibling-gated rule as a miss. It read `blk.N.proj` as unnamed
 * on a repo where the app names it — a measurement that disagrees with the
 * product it is measuring, in the direction that makes coverage work look
 * unfinished. `model-coverage.ts` was always on this path via `toLogicalTensors`.
 */
export function classifyAll(
  tensors: readonly InventoryTensor[],
  family: string | undefined,
  anchors: ArchitectureAnchors | undefined,
): Map<string, Classification> {
  const placed = new Map<string, Classification>();
  for (const block of blocksByLayer(tensors, family, anchors).values()) {
    for (const [name, classification] of classifyLayer(block)) {
      placed.set(name, classification);
    }
  }
  return placed;
}

/** A classified name, in the shape the grid and the tensor list consume. */
function placementOf(
  name: string,
  classified: ReadonlyMap<string, Classification>,
): TensorPlacement {
  const found = classified.get(name);
  return {
    layer: layerGroupOf(name).layer,
    role: found?.role ?? "other",
    rule: found?.rule ?? "no rule matched",
  };
}

export function toLogicalTensors(
  tensors: readonly InventoryTensor[],
  declared?: DeclaredQuantization,
  anchors?: ArchitectureAnchors,
  family?: string,
): LogicalTensor[] {
  const classified = classifyAll(tensors, family, anchors);
  // Resolution lives in `mlx.ts`, which was verified byte-for-byte against
  // MLX's own bit extraction on real payloads. Doing it inline here would mean
  // a second, weaker implementation of the same arithmetic — and the weaker
  // version silently accepted a wrong bits/group split, because the shapes
  // constrain only the product.
  const scan = findAffineGroups(tensors, declared, anchors);
  const byPrefix = new Map(scan.groups.map((g) => [g.prefix, g]));
  // AWQ and GPTQ, which pack into int32 and are not MLX's scheme. Keyed on
  // `qweight`, so the two scans cannot both claim a prefix.
  const packedByPrefix = new Map(
    findPackedIntGroups(tensors, declared).map((g) => [g.prefix, g]),
  );
  /*
   * MXFP4's `blocks`/`scales` pairs, which `baseName` cannot fold.
   *
   * The other two schemes suffix with a dot, so stripping a known suffix off a
   * name is enough to group them. These cannot be stripped blindly in either
   * spelling — a bare `scales` suffix would swallow any tensor whose name
   * merely ends that way, folding it into a prefix that may hold nothing else.
   * So the grouping is driven by the *verified pairs* instead: a name is
   * rewritten only when `findBlockScaledGroups` has already checked that its
   * partner exists and that the block arithmetic comes out.
   *
   * This said "these use an underscore" until 2026-08-15, which was true of
   * every repo we had looked at and false of `gpt-oss`'s own `original/`
   * variant — the defect that sent that model's parameter count 42.8% light.
   * The separator is now `_` or `.`; see `SEPARATORS` in `block-scaled.ts`.
   */
  const blockScaled = findBlockScaledGroups(tensors, declared);
  const blockScaledByPrefix = new Map(blockScaled.map((g) => [g.prefix, g]));
  /*
   * The same idea under compressed-tensors' naming: `weight_packed` beside
   * `weight_scale`, both `U8`. Folded on the same terms — a verified pair or
   * nothing — because the failure is identical: two byte planes counted as
   * elements report a 4-bit checkpoint as an 8-bit one.
   */
  const packedWeights = findPackedWeightGroups(tensors, declared);
  const packedWeightsByPrefix = new Map(packedWeights.map((g) => [g.prefix, g]));
  /*
   * The same idea a third time, spelled `.weight` beside `.scale` with an
   * `F8_E8M0` scale plane — `deepseek-ai/DeepSeek-V4-Flash`. `.scale` singular
   * is deliberately not in `PART_SUFFIXES`, so these did not group at all and
   * the payload reported its byte count as its parameter count. This folder
   * takes no `declared`: that repo's config says `fp8`, which is true of its
   * attention and wrong about its experts. See `e8m0-scaled.ts`.
   */
  const e8m0Scaled = findE8m0ScaledGroups(tensors);
  const e8m0ByPrefix = new Map(e8m0Scaled.map((g) => [g.prefix, g]));
  /*
   * EXL3 trellis — the sixth packed format, and the first that is recognized
   * without being folded.
   *
   * The shapes appear to determine the logical matrix, and 409 of 409 modules
   * across one checkpoint agree with that reading. It is still an estimate
   * rather than a measurement: every folder above was validated against ground
   * truth, and structural self-consistency is a model agreeing with itself.
   * Reviewer ruling, 2026-08-17 — recognize, refuse, and state the finding as
   * pending. See `analysis/exl3-trellis.ts`.
   */
  const exl3 = findExl3Groups(tensors);
  const exl3ByPrefix = new Map(exl3.map((g) => [g.prefix, g]));
  /*
   * bitsandbytes NF4/FP4, whose sidecars are *children* of `.weight` rather
   * than its siblings — `weight.absmax`, `weight.quant_map`, and four more.
   * `baseName` cannot fold those, and a name list cannot find them: the anchor
   * moves three ways across the four families. So membership comes from the
   * module path and the group is only built once the checkpoint has named its
   * own scheme and the two parameter accounts agree. See `bnb-nf4.ts`.
   */
  const nf4 = findNf4Groups(tensors);
  const nf4ByPrefix = new Map(nf4.map((g) => [g.prefix, g]));
  /*
   * `eschamoe`: six tensors per expert module, codes in `I16`. Without this the
   * routed experts — 32.2B of ~34.7B logical parameters on
   * `EschaLabs/Qwen3.6-35B-A3B-Escha-W2` — report 16 bits per weight on a repo
   * whose own name says W2. Takes no `declared`: the six-tensor shape is
   * self-describing. See `eschamoe.ts`.
   */
  const escha = findEschaMoeGroups(tensors);
  const eschaByPrefix = new Map(escha.map((g) => [g.prefix, g]));
  /*
   * `weight_int8` beside `weight_scale`, on the same repo's attention and
   * shared experts. The parameter count is already right — one byte is one
   * weight — so this folds for cost and membership only: without it the scale
   * plane is a logical tensor of its own and the payload states 8.00 bpw, the
   * number a reader gets from the dtype alone. See `int8-scaled.ts`.
   */
  const int8 = findInt8ScaledGroups(tensors);
  const int8ByPrefix = new Map(int8.map((g) => [g.prefix, g]));
  const foldedInto = new Map<string, string>();
  for (const group of [
    ...blockScaled,
    ...packedWeights,
    ...e8m0Scaled,
    ...nf4,
    ...escha,
    ...int8,
  ]) {
    for (const part of group.partNames) foldedInto.set(part, group.prefix);
  }

  const groups = new Map<string, InventoryTensor[]>();
  for (const tensor of tensors) {
    const key = foldedInto.get(tensor.name) ?? baseName(tensor.name);
    const bucket = groups.get(key);
    if (bucket) bucket.push(tensor);
    else groups.set(key, [tensor]);
  }

  const logical: LogicalTensor[] = [];
  for (const [name, parts] of groups) {
    const bytes = parts.reduce((sum, p) => sum + p.byteLength, 0);
    // The payload part, not a scale/bias plane — it carries the storage dtype
    // and the packed shape the cross-check needs.
    /*
     * The payload, not merely the first part.
     *
     * `parts[0]` is whatever the safetensors header happened to list first, and
     * for a GPTQ group that is `g_idx` — so the logical tensor reported the
     * group-index plane as its main part, and a click on its cell in the map
     * selected the index rather than the weights. Reported from the dev server
     * as "clicking the map gives an error while clicking `qweight` in the list
     * works" (2026-08-08).
     *
     * `.qweight` sits beside `.weight` because it is the same thing under a
     * different scheme: the tensor the others describe.
     */
    const main =
      parts.find((p) => p.name === `${name}.weight`) ??
      parts.find((p) => p.name === `${name}.qweight`) ??
      // MXFP4's payload. Same reasoning as `.qweight`: the tensor the others
      // describe, and the one a click on this cell should open.
      parts.find((p) => p.name === `${name}_blocks`) ??
      // compressed-tensors' payload, same reasoning as `_blocks`.
      parts.find((p) => p.name === `${name}_packed`) ??
      /*
       * EXL3's payload. Without this the fallback to `parts[0]` picked whichever
       * part the header happened to list first — `.mcg`, a scalar — so the map
       * painted every EXL3 cell with a *sidecar's* dtype (`F16` where the
       * payload is `I16`) and a click on the cell opened the scalar instead of
       * the weights.
       *
       * Identical to the `g_idx` defect this chain's own comment describes,
       * one format later. Found in the browser rather than by a test: the
       * arithmetic was already right, and only the legend showed it.
       */
      parts.find((p) => p.name === `${name}.trellis`) ??
      parts.find((p) => p.name === name) ??
      parts[0];
    if (!main) continue;

    const affine = byPrefix.get(name);
    let params = parts.reduce((sum, p) => sum + p.numel, 0);
    let quantLabel: string | undefined;
    let crossCheckError: string | undefined;
    let ambiguous = false;

    /*
     * A non-parameter buffer, decided before any quantization scheme is
     * considered.
     *
     * Order matters and this has to come first: every branch below recovers a
     * *logical* parameter count from a packed one, and none of them is
     * meaningful for a tensor that holds no parameters at all. A buffer is also
     * never packed — masks and frequency vectors ship as plain F32 — so nothing
     * downstream loses a case by being skipped here.
     *
     * Keyed on the payload part rather than the group name, because that is the
     * physical tensor whose shape the rule tests. The group name has had its
     * suffix stripped, and `h.0.attn` is not a name any buffer rule matches.
     */
    const buffer = classifyBuffer(main);
    if (buffer) {
      /*
       * Zero, not `numel`. The elements exist and are stored; they are not
       * weights, and `params` is the answer to "how many parameters does this
       * model have". `bytes` above is untouched — a byte is a byte, and the
       * byte total has to keep reconciling with the file size on the Hub.
       */
      params = 0;
    }

    const packed = packedByPrefix.get(name);
    const exl3Group = exl3ByPrefix.get(name);
    if (exl3Group) {
      /*
       * EXL3, folded — the sixth packed format to recover a logical count from
       * a packed container.
       *
       * Verified before it was allowed to count: the width is exactly what
       * exllamav3's own loader computes (`trellis.shape[-1] // 16`), and the
       * count is the product of the two per-channel scale vectors the format
       * documents. See `analysis/exl3-trellis.ts` for the provenance of each,
       * which differ and are reported as differing.
       *
       * A group whose geometry does **not** reconcile still refuses, on the same
       * footing as an unresolvable MLX triple. That branch is what stops a
       * reading verified against one packing from silently outliving it.
       *
       * Deliberately before the folders below: none of them describes this
       * scheme, and falling through would recover a confident count from the
       * wrong model of the bytes.
       */
      if (exl3Group.resolved) {
        params = exl3Group.resolved.params;
        // The honest half of a `bits@group` label: EXL3 has a width and no
        // group size, so stating one would invent a parameter of the scheme.
        quantLabel = `${exl3Group.resolved.bitsPerWeight}-bit`;
      } else {
        ambiguous = true;
        params = 0;
      }
    } else if (buffer) {
      /*
       * Nothing to recover. Every branch below reconstructs a logical parameter
       * count from a packed one, and a buffer has no parameters to reconstruct
       * — so this arm exists to make that skip structural rather than relying
       * on a buffer never appearing in one of the group maps. It does not
       * today; a future folder keyed on a shape rather than a suffix could
       * change that, and would silently restore the elements this fix removes.
       */
    } else if (packed) {
      /*
       * The logical matrix, recovered from the packing axis. Without this the
       * cell reports the *packed* element count and a nominal 32 bits per
       * weight — the quiet failure this scheme was reported for.
       *
       * `bytes` deliberately stays the sum of every part, so the figure answers
       * the question the reporter actually wanted: how much of the scheme's
       * budget is codebook rather than weights. `qweight` alone is exactly
       * `bits`; the difference is the group overhead.
       */
      params = packed.params;
      quantLabel = `${packed.bits}@${packed.groupSize}`;
      crossCheckError = packed.disagreement;
    } else if (blockScaledByPrefix.has(name)) {
      /*
       * Both planes are `U8`, so the element count is the *byte* count and the
       * cell reads 8.00 bits per weight — a model's entire bulk mass painted as
       * an 8-bit checkpoint when it is a 4.25-bit one. The pair's geometry was
       * verified before it was folded; see `block-scaled.ts`.
       */
      const group = blockScaledByPrefix.get(name) as BlockScaledGroup;
      params = group.params;
      quantLabel = `${group.bits}@${group.groupSize}`;
    } else if (packedWeightsByPrefix.has(name)) {
      /*
       * `moonshotai/Kimi-K3`: `weight_packed` and `weight_scale`, both `U8`, so
       * the element count is the byte count and the model read 8.30 bits per
       * weight instead of 4.49 — on the flagship repo, on its share card, in
       * contradiction of its own model card. The pair was verified before it
       * was folded; see `compressed-tensors.ts`.
       *
       * `resolved` is undefined when the repo's declared widths all fit these
       * shapes, which is the mixed-precision case failing to narrow. Handled
       * exactly as the affine branch below handles its own unresolved split,
       * and for the same reason: a wrong parameter count scales the headline
       * bits per weight by precisely the factor it is wrong by.
       */
      const group = packedWeightsByPrefix.get(name) as PackedWeightGroup;
      crossCheckError = group.disagreement;
      if (group.resolved) {
        params = group.resolved.params;
        quantLabel = `${group.resolved.bits}@${group.resolved.groupSize}`;
      } else {
        ambiguous = true;
        params = 0;
      }
    } else if (eschaByPrefix.has(name)) {
      /*
       * The codes are `I16`, so the naive element sum reads the payload's word
       * count as its weight count and every expert module reports 16 bpw. The
       * logical shape is in the scale planes — `[E, in]` and `[E, out]` — and
       * the recovered width lands exactly on the per-module figure the config
       * declares, 2.0 and 3.0. Verified before folding; see `eschamoe.ts`.
       */
      const group = eschaByPrefix.get(name) as EschaMoeGroup;
      params = group.params;
      // No group size to state: an additive codebook has stages, not groups.
      // The width is the honest half of the usual `bits@group` label.
      quantLabel = `${group.bits}-bit`;
    } else if (int8ByPrefix.has(name)) {
      /*
       * The naive sum would now include the scale plane's elements, because the
       * fold put it in this group — so the count has to come from the payload
       * rather than from the parts. Same number as before the fold for the
       * payload itself; the difference is that it stays right.
       */
      const group = int8ByPrefix.get(name) as Int8ScaledGroup;
      params = group.params;
      quantLabel = "8-bit";
    } else if (nf4ByPrefix.has(name)) {
      /*
       * `unsloth/llama-3-8b-bnb-4bit`: a flat `U8` payload whose element count
       * is its byte count, so every 4-bit projection read **8.00** bits per
       * weight against a true 4.127. Two-fold wrong rather than eightfold,
       * which is why it survived: a 4-bit repo reporting 8.00 reads as one that
       * shipped int8, not as a bug.
       *
       * Params come from the payload and are cross-checked against `absmax`
       * before the group forms, so this needs no payload read; see `bnb-nf4.ts`.
       */
      const group = nf4ByPrefix.get(name) as Nf4Group;
      params = group.params;
      quantLabel = `${group.bits}@${group.groupSize}`;
    } else if (e8m0ByPrefix.has(name)) {
      /*
       * `deepseek-ai/DeepSeek-V4-Flash`: an `I8` payload beside an `F8_E8M0`
       * scale plane. The two never grouped, so the payload's *bytes* were its
       * parameter count and its biggest rows read 8.0 bits per weight where the
       * model stores 4.25. The scale dtype is what fixes the block at 32 and so
       * resolves the width the shapes leave open; see `e8m0-scaled.ts`.
       */
      const group = e8m0ByPrefix.get(name) as E8m0ScaledGroup;
      params = group.params;
      quantLabel = `${group.bits}@${group.groupSize}`;
    } else if (affine) {
      crossCheckError = affine.disagreement;
      if (affine.resolved) {
        // The logical matrix, not the packed one: this is what makes effective
        // bits-per-weight mean anything.
        params = affine.rows * affine.resolved.cols;
        quantLabel = `${affine.resolved.bits}@${affine.resolved.groupSize}`;
      } else {
        // Neither the shapes nor the declaration pin it down. Refuse to invent
        // a parameter count — being wrong here scales the headline bpw by the
        // exact factor the bits/group split is wrong by.
        ambiguous = true;
        params = 0;
      }
    }

    const placement = placementOf(main.name, classified);
    logical.push({
      name,
      payloadName: main.name,
      // Look up on the physical name: some rules key off `.weight`/`.bias`,
      // which the base name has had stripped.
      placement: buffer
        ? /*
           * The rule always changes — a reader who sees a tensor excluded from
           * the parameter count is owed the reason — but the **role** changes
           * only where the name table got it wrong.
           *
           * `rope_freqs` keeps `rope`, which is accurate and more specific than
           * any bucket this could move it to; the causal mask does not keep
           * `bias`, which is a false statement about the tensor. That decision
           * belongs to the rule that knows the shape, not here; see `role` on
           * `BufferRule`.
           *
           * The override lives here rather than as a row at the top of
           * `ROLE_RULES` because that table matches names alone, and every
           * buffer rule is a name *and* a shape. Expressing "`attn.bias`, but
           * only when it is `1×1×N×N`" as a role pattern would either drop the
           * shape half — the half that stops a genuine bias vector being
           * deleted from the count — or teach a name table about shapes, which
           * is the one thing its contract says it does not do.
           */
          { ...placement, role: buffer.role ?? placement.role, rule: buffer.rule }
        : placement,
      parts,
      bytes,
      params,
      // Guard the division: a zero-parameter tensor is degenerate, and an
      // Infinity here would poison the colour scale for the whole grid.
      effectiveBpw: params > 0 ? (bytes * 8) / params : 0,
      dtype: main.dtype,
      quantLabel,
      crossCheckError,
      ambiguous,
      // Recognized *and* refused — not merely recognized. A folded EXL3
      // group is resolved, so it is neither ambiguous nor declined.
      declined: exl3Group !== undefined && exl3Group.resolved === undefined,
      isBuffer: buffer !== undefined,
    });
  }
  return logical;
}

/**
 * How many parameters this model has. The one place to ask.
 *
 * ## Why this function exists rather than a `reduce` at each call site
 *
 * A parameter count is **packing-sensitive**, and this codebase has now got it
 * wrong five times in the same shape. Each of AWQ, GPTQ, MXFP4, NF4 and
 * compressed-tensors `pack-quantized` was fixed inside `toLogicalTensors` —
 * which is what the map, the ledger and the share card read — while the status
 * line went on summing `numel` straight off the header. So all five fixes left
 * it wrong, and worse, they made the page *contradict itself*: before them the
 * two agreed because neither folded.
 *
 * What that shipped:
 *
 *     openai/gpt-oss-20b                   status line 11.96B   map 20.91B
 *     unsloth/llama-3-8b-bnb-4bit                      4.65B         8.03B
 *     Qwen/Qwen2.5-7B-Instruct-AWQ                     1.96B         7.62B
 *     kaitchup/Qwen3.6-27B-autoscheme-3.5bit-XS        4.65B        27.36B
 *     gpt2                                             0.14B         0.14B
 *
 * Every one of those repos states its own size in its name, and `gpt2` landing
 * on exactly 1.00x is what pins the cause to packing rather than to arithmetic:
 * an unquantized repo's stored elements *are* its parameters.
 *
 * The reviewer's general form, which this function is the enforcement of: **a
 * packing-sensitive quantity has one source of truth, and every display reads
 * the folded view or declares why not.** Element counts are that class; bytes
 * are safe by nature, because a byte is a byte however it is packed.
 *
 * ## Whole model, never a tower
 *
 * Deliberately unfiltered, where `anatomyOf` scopes to the selected stack. The
 * status line describes the *repo* — a figure that changed when you clicked a
 * tower chip would be a third number with the same name.
 *
 * ## It is a no-op on the formats that were already right
 *
 * GGUF headers declare **logical** dims and get their byte length from the ggml
 * block size, so `numel` there is already the parameter count. Nothing in a
 * GGUF name folds, so every tensor lands in its own group and the total comes
 * back identical — the same call is correct for both formats and needs no
 * branch. `gpt2` is the same story for safetensors. Both are asserted in
 * `anatomy.test.ts`, because "this changed nothing" is the half of the claim
 * that is easy to break later.
 *
 * Costs no requests: headers are in hand and the config was already read.
 */
export function modelParamCount(
  tensors: readonly InventoryTensor[],
  declared?: DeclaredQuantization,
  anchors?: ArchitectureAnchors,
  family?: string,
): number {
  return toLogicalTensors(tensors, declared, anchors, family).reduce(
    (sum, logical) => sum + logical.params,
    0,
  );
}

/**
 * A cell's distinct dtypes, **heaviest first**.
 *
 * The dtype map paints `dtypes[0]`, so this ordering decides what a cell claims
 * to be stored as — and it used to be first-seen order, which is not a fact
 * about the model at all, only about iteration.
 *
 * Reported from production on `deepseek-ai/DeepSeek-V4-Flash-0731` (Reddit,
 * 2026-08-08). Its experts store `w1.weight` as `I8 [2048,2048]` beside a
 * `w1.scale` of `F8_E8M0 [2048,128]` — one block-scale sidecar per weight, a
 * sixteenth of its elements and a sixteenth of its bytes. Both land in the same
 * role cell, the scale happened to be enumerated first, and so every `mlp.*`
 * cell in the map announced `F8_E8M0`. `I8` is 88.8% of that checkpoint's bytes
 * and appeared in the legend having painted **nothing**.
 *
 * Ordering by bytes is not a tie-break heuristic: a sidecar is by construction a
 * small fraction of what it scales, so the tensor that dominates the cell's
 * bytes is the one the cell is *about*. It fixes every block-scaled fp8 repo at
 * once — the modern DeepSeek lineage and everything copying it — rather than
 * this one model.
 *
 * Ties break on the dtype name so the output stays deterministic, which the
 * card golden depends on.
 */
function dtypesByBytes(tensors: readonly LogicalTensor[]): string[] {
  const bytes = new Map<string, number>();
  for (const tensor of tensors) {
    bytes.set(tensor.dtype, (bytes.get(tensor.dtype) ?? 0) + tensor.bytes);
  }
  return [...bytes]
    .sort(
      ([nameA, bytesA], [nameB, bytesB]) => bytesB - bytesA || (nameA < nameB ? -1 : 1),
    )
    .map(([name]) => name);
}

export interface AnatomyCell {
  readonly layer: number | undefined;
  readonly role: string;
  readonly tensors: readonly LogicalTensor[];
  readonly bytes: number;
  readonly params: number;
  readonly effectiveBpw: number;
  /**
   * Distinct storage dtypes, **ordered by the bytes each occupies**, so
   * `dtypes[0]` is what the cell is mostly made of. See `dtypesByBytes`.
   */
  readonly dtypes: readonly string[];
  readonly quantLabel: string | undefined;
  readonly crossCheckError: string | undefined;
  /** Any part of this cell had an unresolvable quantization scheme. */
  readonly ambiguous: boolean;
  /**
   * Every tensor here is a non-parameter buffer, so the cell's bytes belong to
   * the file total but not to the weight total. See `analysis/buffers.ts`.
   */
  readonly isBuffer: boolean;
  /**
   * Bytes held by tensors in this cell whose parameter count is **unknown** —
   * an unresolvable quantized triple, or a packed format we recognize and
   * refuse (EXL3).
   *
   * Tracked separately because a cell can hold both kinds. `mlp.down` on
   * `malaiwah/Qwen3.8-27B-EXL3-K5K6-hydrated` holds an `I16` trellis with no
   * knowable parameter count *and* a `BF16` bias with a real one, and dividing
   * the cell's whole byte count by only the parameters it can account for
   * reported **231.81 bits/weight**. The headline came out at 46.13.
   *
   * This is the same identity the buffer work established — params, bytes and
   * bits/weight must come from one set — applied to the other kind of
   * zero-parameter tensor. It predates EXL3: any model mixing an ambiguous
   * tensor with a resolved one in a single cell was inflated the same way, and
   * refusing to count the trellis is what made it visible.
   */
  readonly unresolvedBytes: number;
}

export interface Anatomy {
  /** Ascending layer indices, then `undefined` last for the non-stack rows. */
  readonly layers: readonly (number | undefined)[];
  readonly roles: readonly string[];
  readonly cells: ReadonlyMap<string, AnatomyCell>;
  /** `bpw` once a scheme is recognized; `dtype` otherwise. */
  readonly mode: "bpw" | "dtype";
  readonly minBpw: number;
  readonly maxBpw: number;
  /** Rules that actually fired, so the grid can show its working. */
  readonly rulesUsed: readonly string[];
  readonly crossCheckErrors: readonly string[];
  /** Quantized triples whose (bits, group) the evidence could not pin down. */
  readonly ambiguousCount: number;
  /**
   * Of those, how many were declined by a recognized format rather than left
   * genuinely unresolved. See `declined` on `LogicalTensor`.
   */
  readonly declinedCount: number;
  /** Names that matched no rule — an honest count of what we didn't understand. */
  readonly unclassified: number;
  /** Payload-vs-codebook split, when any tensor resolved to a scheme. */
  readonly budget: QuantizationBudget | undefined;
}

/**
 * A cell's identity: **(layer, role) inside one layer stack.**
 *
 * The last four words are a precondition on the caller, not a description of
 * the string. A layer index only means something within its own stack (see
 * `analysis/towers.ts`), so on a multimodal checkpoint built as one grid this
 * key merges audio layer 3, text layer 3 and vision layer 3 into a single cell
 * — three unrelated tensors under one heading, with their bytes summed and
 * their bits-per-weight recomputed into a number describing nothing.
 *
 * The fix is structural rather than a wider key: `buildAnatomy` is handed **one
 * tower's tensors at a time**, where a layer index is unambiguous by
 * construction and the collision cannot arise. `main.ts` owns that split and
 * the tower chips over the list choose which stack — which is also why there is
 * no "all" chip to choose. Widening the key would instead have kept the merged
 * grid addressable, and the merged grid *is* the defect.
 *
 * The separator is written as an escape and never as a literal byte: a raw NUL
 * makes `grep` treat the whole file as binary and report *no matches* — not an
 * error, just silence. Same reason as `GROUP_SEPARATOR` in `main.ts`; `pnpm
 * check-no-nul` is the gate.
 */
export function cellKey(layer: number | undefined, role: string): string {
  return `${layer ?? "-"}\u0000${role}`;
}

/**
 * Build the grid.
 *
 * Roles are ordered by a fixed preference list rather than by frequency, so the
 * same architecture always lays out the same way and two models can be compared
 * by eye. Anything unrecognized is appended, never dropped.
 */
const ROLE_ORDER = [
  "embed",
  "pos-embed",
  "attn.q",
  "attn.k",
  "attn.v",
  "attn.qkv",
  // Compressed KV, as MLA-lineage models store it: a genuinely different
  // position from separate k and v, and it reads at thumbnail size.
  "attn.kv",
  "attn.o",
  /*
   * An output gate on the attention result, before the output projection.
   *
   * Its own role because no existing one is an identity: `attn.o` is taken by a
   * co-resident `o_proj` in every repo that has it, and `mlp.gate` is a
   * feed-forward role. 231 tensors across 6 repos and 4 unrelated lineages,
   * always a sibling of `q/k/v/o_proj` and never present without them — and on
   * `Kimi-K3` it survives the change of token mixer between delta-net and MLA
   * layers, which is what says it belongs to the attention block rather than to
   * the mixer's state machinery.
   *
   * Granularity is not readable from the name: Kimi's is `[12288,7168]`, one
   * channel per activation, while `poolside/Laguna-S-2.1` is `[72,3072]`, one
   * per head. Both are output gates; only the shape distinguishes them.
   */
  "attn.gate",
  // Sits with the attention rows because that is what it is, and last among
  // them because it is the one the classifier could not place precisely.
  "attn.lowrank",
  "mlp.gate",
  "mlp.up",
  // Fused gate+up, as MoE and several dense models store it. Its own role for
  // the same reason `attn.qkv` has one: the tensor holds `2I x H`, so filing it
  // under either half would claim a matrix that is not in the file and halve
  // the parameters of the role it landed in — 244 GiB misattributed on one
  // checkpoint alone.
  "mlp.gate_up",
  "mlp.down",
  "moe.router",
  "norm",
  "bias",
  /*
   * Tensors in the file that are **not parameters** — see `analysis/buffers.ts`.
   *
   * Its own row rather than a share of `bias`, which is where gpt2's twelve
   * causal masks used to sit: 48.0 MiB of lower-triangular ones filed beside
   * 55 KiB of genuine bias vectors, so the row read as though gpt2 had 48 MiB
   * of biases and the 12.6M elements it contributed to the parameter count were
   * invisible. Excluding them from `params` without giving them somewhere to
   * appear would have moved the defect rather than fixed it: the bytes are
   * really in the file and a reader is entitled to see where they went.
   */
  "buffer",
  "lm_head",
  "other",
];

/**
 * Where a quantized checkpoint's bits actually go.
 *
 * ## The question this answers
 *
 * From the reader who reported the AWQ/GPTQ misreading (launch post,
 * 2026-08-08), on being shown the fix: *"not what a tensor is stored at, but
 * how much of a scheme's budget goes to the codebook instead of the weights.
 * On these four, between 3.9% and 4.3%."* That is a better question than the one
 * this tool was answering, and every number it needs was already being computed
 * — just never put on screen.
 *
 * ## Why one rule covers every scheme
 *
 * A quantized tensor is a payload plus the metadata needed to interpret it, and
 * `payloadName` already names the payload for exactly the reason that mattered
 * elsewhere: `parts[0]` is header order, which for GPTQ is `g_idx`. So the
 * split is *payload vs. everything else in the group*, with no per-scheme
 * branch:
 *
 *     MLX affine   .weight            vs  .scales + .biases
 *     AWQ          .qweight           vs  .scales + .qzeros
 *     GPTQ         .qweight           vs  .scales + .qzeros + .g_idx
 *     MXFP4        _blocks            vs  _scales
 *
 * The payload figure lands on the nominal width every time — a round 4.00 on
 * all four above — which is what makes it a check and not just a display: the
 * remainder is the overhead, and it is the part that differs between schemes.
 * GPTQ carries more than AWQ because `g_idx` is another int32 per input
 * feature; MXFP4's 5.9% is one E8M0 byte per 32 values.
 *
 * Ambiguous tensors are excluded rather than counted at zero: their parameter
 * count is unknown, so including them would drag the ratio toward a number
 * nobody could defend. `tensors` says how many did count.
 */
export interface QuantizationBudget {
  /** Logical weights across every tensor with a resolved scheme. */
  readonly params: number;
  /** Payload bytes — the weights themselves. */
  readonly payloadBytes: number;
  /** Everything else in those groups: scales, zero points, group indices. */
  readonly codebookBytes: number;
  /** Payload plus codebook, as bits per logical weight. */
  readonly effectiveBpw: number;
  /** The nominal width — 4.00 on a 4-bit scheme, by construction. */
  readonly payloadBpw: number;
  /** What the scheme spends above nominal. */
  readonly codebookBpw: number;
  /** Codebook as a fraction of the whole budget. */
  readonly codebookShare: number;
  /** How many logical tensors this covers. */
  readonly tensors: number;
}

/** `undefined` when no tensor resolved to a scheme — there is no budget to show. */
export function quantizationBudget(
  logical: readonly LogicalTensor[],
): QuantizationBudget | undefined {
  let params = 0;
  let payloadBytes = 0;
  let codebookBytes = 0;
  let tensors = 0;

  for (const tensor of logical) {
    // A resolved scheme and a known denominator. `ambiguous` fails the second.
    if (!tensor.quantLabel || tensor.params <= 0) continue;
    const payload = tensor.parts.find((p) => p.name === tensor.payloadName);
    if (!payload) continue;
    params += tensor.params;
    payloadBytes += payload.byteLength;
    codebookBytes += tensor.bytes - payload.byteLength;
    tensors += 1;
  }

  if (tensors === 0 || params <= 0) return undefined;
  const bytes = payloadBytes + codebookBytes;
  return {
    params,
    payloadBytes,
    codebookBytes,
    effectiveBpw: (bytes * 8) / params,
    payloadBpw: (payloadBytes * 8) / params,
    codebookBpw: (codebookBytes * 8) / params,
    codebookShare: bytes > 0 ? codebookBytes / bytes : 0,
    tensors,
  };
}

/** Bytes in a cell whose parameter count is unknown. See `AnatomyCell`. */
function unresolvedBytesFor(parts: readonly LogicalTensor[]): number {
  let n = 0;
  for (const t of parts) if (t.ambiguous) n += t.bytes;
  return n;
}

export function buildAnatomy(logical: readonly LogicalTensor[]): Anatomy {
  const cells = new Map<string, AnatomyCell>();
  const layerSet = new Set<number>();
  const roleSet = new Set<string>();
  const rulesUsed = new Set<string>();
  const crossCheckErrors: string[] = [];
  let hasNonStack = false;
  let unclassified = 0;
  let anyQuant = false;
  let ambiguousCount = 0;
  let declinedCount = 0;

  for (const tensor of logical) {
    const { layer, role, rule } = tensor.placement;
    rulesUsed.add(rule);
    if (role === "other") unclassified += 1;
    if (tensor.quantLabel) anyQuant = true;
    if (tensor.ambiguous) ambiguousCount += 1;
    if (tensor.declined) declinedCount += 1;
    if (tensor.crossCheckError) {
      crossCheckErrors.push(`${tensor.name}: ${tensor.crossCheckError}`);
    }
    if (layer === undefined) hasNonStack = true;
    else layerSet.add(layer);
    roleSet.add(role);

    const key = cellKey(layer, role);
    const existing = cells.get(key);
    const parts = existing ? [...existing.tensors, tensor] : [tensor];
    const bytes = (existing?.bytes ?? 0) + tensor.bytes;
    const params = (existing?.params ?? 0) + tensor.params;
    const dtypes = [...new Set([...(existing?.dtypes ?? []), tensor.dtype])];

    cells.set(key, {
      layer,
      role,
      tensors: parts,
      bytes,
      params,
      /*
       * Accountable bytes over accountable params. Subtracting
       * `unresolvedBytes` is what stops a cell holding an undecoded payload
       * beside a real bias from charging the payload's bytes to the bias.
       */
      effectiveBpw: params > 0 ? ((bytes - unresolvedBytesFor(parts)) * 8) / params : 0,
      dtypes,
      quantLabel: tensor.quantLabel ?? existing?.quantLabel,
      crossCheckError: tensor.crossCheckError ?? existing?.crossCheckError,
      ambiguous: tensor.ambiguous || (existing?.ambiguous ?? false),
      /*
       * `every`, not `some`, and the difference is the whole point.
       *
       * A cell is a buffer cell only if **nothing** in it is a parameter. The
       * role override in `toLogicalTensors` already guarantees buffers land in
       * `buffer` and nothing else does, so today this is always all-or-nothing
       * — but `anatomyTotals` uses this flag to decide whether to keep a cell's
       * bytes out of the weight total, and `some` would silently discard real
       * weight bytes the moment one buffer shared a cell with them.
       */
      isBuffer: tensor.isBuffer && (existing?.isBuffer ?? true),
      // Accumulated per tensor, not per cell: `ambiguous` on the cell is a
      // disjunction over its tensors, so it cannot say how many bytes the
      // unknown ones account for.
      unresolvedBytes:
        (existing?.unresolvedBytes ?? 0) + (tensor.ambiguous ? tensor.bytes : 0),
    });
  }

  // Order every cell's dtypes by the bytes they occupy, now that each cell has
  // all of its tensors. See `dtypesByBytes` for the bug this fixes.
  for (const [key, cell] of cells) {
    cells.set(key, { ...cell, dtypes: dtypesByBytes(cell.tensors) });
  }

  const layers: (number | undefined)[] = [...layerSet].sort((a, b) => a - b);
  if (hasNonStack) layers.push(undefined);

  /*
   * `other` last, always — after the roles this table does not know about.
   *
   * The order is a curated list plus an alphabetical tail, and `other` used to
   * sit at the end of the *curated* part, which put it above every role added
   * since. On a Gemma 4 card that read: … norm, other, ple.embed, ple.gate,
   * ple.model_proj, ple.proj — the unrecognized bucket wedged into the middle of
   * the model, with named rows below it.
   *
   * It is not a component and it does not belong in a sequence that otherwise
   * runs embed → attention → feed-forward → norms. It is the residue, and the
   * residue goes at the bottom where a reader can stop before reaching it.
   * Pulled out of the sort entirely rather than moved down `ROLE_ORDER`, so a
   * role added to that table later cannot push it back up.
   */
  const named = ROLE_ORDER.filter((r) => r !== "other" && roleSet.has(r));
  const rest = [...roleSet]
    .filter((r) => r !== "other" && !ROLE_ORDER.includes(r))
    .sort();
  const roles = [...named, ...rest, ...(roleSet.has("other") ? ["other"] : [])];

  let minBpw = Number.POSITIVE_INFINITY;
  let maxBpw = 0;
  for (const cell of cells.values()) {
    if (cell.effectiveBpw <= 0) continue;
    minBpw = Math.min(minBpw, cell.effectiveBpw);
    maxBpw = Math.max(maxBpw, cell.effectiveBpw);
  }
  if (!Number.isFinite(minBpw)) minBpw = 0;

  return {
    layers,
    roles,
    cells,
    mode: anyQuant ? "bpw" : "dtype",
    minBpw,
    maxBpw,
    rulesUsed: [...rulesUsed],
    crossCheckErrors,
    ambiguousCount,
    declinedCount,
    unclassified,
    budget: quantizationBudget(logical),
  };
}

/**
 * Why this anatomy is not a whole model, or `undefined` when it is one.
 *
 * ## The question a visitor actually arrives with
 *
 * `lukaskremla/Qwen3.8-27B-MTP-3bit-MLX` is 186 MB and its name says **27B**.
 * People open it to find out how that can be, and the page already answers them
 * — `31 tensors · 425M params · 177 MiB` — without ever saying that *is* the
 * answer. The interesting fact is that this is not the model it is named after,
 * and the reader was left to infer it from a number.
 *
 * MTP heads are the steadiest new population on the site (eight of ten stuck
 * card keys since 2026-08-19), so this is a recurring confusion rather than an
 * edge case.
 *
 * ## Structure only, and deliberately
 *
 * Two facts from the anatomy the page has already built, and nothing else:
 *
 * - **At most one repeating layer.** A whole transformer has a stack.
 * - **No `embed` role.** A model that cannot turn tokens into vectors cannot run
 *   alone, whatever else it contains. This is the corroboration that keeps the
 *   rule off genuine one-layer toy models (`tiny-random-*`), which *do* carry an
 *   embedding table (reviewer, 2026-08-30).
 *
 * It reads no config, needs no architecture, and bumps no cache version — the
 * anatomy already knows both. That also makes it the honest answer for
 * `mlx-works/Qwen3.8-27B-oQ2e-mtp`, whose **name** says `-mtp`, whose config
 * says plain `qwen3_5`, and whose structure says one layer: this claims only the
 * third, which is the only one the project's own doctrine lets us reason from
 * (`gguf/variants.ts` — a label is "a name, and nothing more").
 *
 * **It does not say "multi-token prediction".** Structure cannot distinguish an
 * MTP head from a draft head or an adapter, and naming the specific one would
 * need `model_type` plumbed through `Inventory` — a cache version bump for a
 * more precise word, not a truer one. `DRAFT_LABEL` is the vocabulary the tower
 * detector already uses for the same object seen from inside a full model.
 */
export function notAWholeModel(anatomy: Anatomy): string | undefined {
  const layers = anatomy.layers.filter((l) => l !== undefined).length;
  if (layers > 1) return undefined;
  if (anatomy.roles.includes("embed")) return undefined;
  const stack = layers === 0 ? "no repeating layers" : "a single layer";
  return `${stack} and no embedding table`;
}
